import { useCallback, useEffect, useMemo, useReducer, useRef, useState, type ReactNode } from "react";
import { useNavigation } from "@refinedev/core";
import type { TextAreaRef } from "antd/es/input/TextArea";
import { quoteLinkMessage, quoteShareMessage } from "@/services/quote-share";
import {
  Alert,
  Button,
  DatePicker,
  Divider,
  Dropdown,
  Empty,
  Flex,
  Input,
  Mentions,
  Popconfirm,
  Popover,
  Radio,
  Space,
  Spin,
  Tag,
  TimePicker,
  Tooltip,
  Typography,
  message as antdMessage,
} from "antd";
import {
  AudioOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  ContactsOutlined,
  DeleteOutlined,
  DownOutlined,
  EditOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
  MobileOutlined,
  MoreOutlined,
  PaperClipOutlined,
  RedoOutlined,
  SearchOutlined,
  SendOutlined,
  ShopOutlined,
  StopOutlined,
  UpOutlined,
  UserOutlined,
  WarningOutlined,
  WhatsAppOutlined,
} from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import type { Socket } from "socket.io-client";
import {
  ConversationListItem,
  EleccionDeOpcion,
  HISTORY_PAGE,
  MEDIA_MAX_BYTES,
  MEDIA_MAX_MB,
  NoteReplyTo,
  NoteTask,
  NoteView,
  RESPUESTA_LIBRE_MAX,
  ScheduledMessage,
  TaskView,
  TimelineRow,
  conversationsApi,
  mediaSrc,
} from "@/services/conversations.api";

/**
 * Búsqueda del mensaje de un deep-link (Fase 5 de `mensaje-con-link-al-presupuesto`): bloques de
 * `FOCUS_PAGE` hacia atrás en la base, hasta `FOCUS_MAX_PAGES`. 500 mensajes cubren cualquier hilo
 * real; más atrás sólo está el teléfono, y eso lo pide "cargar más" a mano.
 */
const FOCUS_PAGE = 50;
const FOCUS_MAX_PAGES = 10;
import { ContactDetail, ContactMatch, contactsApi, identifierDisplay } from "@/services/contacts.api";
import { PresupuestoIcon, BoxSeam } from "@/components";
import { AgentIcon, SellerIcon, WhatsAppMetaIcon, WhatsAppQrIcon } from "@/components/icon";
import { agentApi } from "@/pages/stores/agent/api";
import { ContextPanel, ContextPanelType } from "./ContextPanel";
import { useOperatorRefs } from "./useOperatorRefs";
import { actualizarNota, puedeElegir } from "./note-choices";
import { mensajeDondeResponder, notaDeFila, ponerNota, ubicarNotas } from "./note-threads";
import { errorAlCancelarConsulta, puedeCancelarConsulta, sinConsulta } from "./cancelar-consulta";
import { citaDeNota, destinoDeLaCita } from "./note-reply";
import {
  actividadAlPie,
  actividadPorNota,
  aplicarTranscripcion,
  type ActividadDelAsistente,
  type EstadoDeTranscripcion,
} from "./agent-activity";
import { ActividadDelAsistenteRow, TranscripcionDelAudio } from "./AgentActivityView";
import {
  FRANJA_VACIA,
  destinoDeLaTarea,
  errorDeTarea,
  fechaDeTarea,
  reducirFranja,
  tareasDeLaNota,
  tareasPorNota,
} from "./agent-tasks";
import { FranjaDeTareas, TareaDeLaNota } from "./AgentTasksView";
import { linkDePresupuesto, linkYaEnviado, partirNota, presupuestosEnNotas } from "./note-links";
import { ownerParticipations } from "./ownerParticipations";
import { contextApi } from "@/services/context.api";
import { estaRestringido, textoVencimiento } from "@/services/channel-restriction";
import {
  MARCADOR_VER_EN_EL_CELULAR,
  capabilitiesOf,
  isCloudApi,
  tiempoRestante,
  ventanaAbierta,
} from "@/services/channel-types";
import { esDeTemplate } from "@/pages/channels/cloud-templates";
import { TemplatePicker } from "./TemplatePicker";
import {
  EfectoDelError,
  RESUMEN_DEL_BLOQUEO,
  aplicarBorrado,
  efectoDelAcuseFallido,
  efectoDelError,
  estaBorrado,
  motivoDeBloqueo,
  ventanaMasReciente,
} from "./cloud-chat";
import { guardarTimeline, leerTimeline, reconciliarTimeline } from "./timeline-cache";
import { toStoredText } from "@/components/history/mention-markers";
import { BRAND, MENTION_BG } from "@/components/history/mention-style";
import {
  EMOJIS_REACCION,
  ReactionUpdate,
  agruparPorEmoji,
  aplicarReaccion,
  emojiAEnviar,
  miReaccion,
} from "./reactions";
import { MentionsInput } from "@/components/history/MentionsInput";

const { Text, Title, Link } = Typography;

const ExpandIcon = () => (
  <svg width="12" height="10" viewBox="0 0 12 10" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 0.25H8C10.0711 0.25 11.75 1.92893 11.75 4V6C11.75 8.07107 10.0711 9.75 8 9.75H4C1.92893 9.75 0.25 8.07107 0.25 6V4C0.25 1.92893 1.92893 0.25 4 0.25Z" fill="white"/>
    <path d="M4 0.25H8C10.0711 0.25 11.75 1.92893 11.75 4V6C11.75 8.07107 10.0711 9.75 8 9.75H4C1.92893 9.75 0.25 8.07107 0.25 6V4C0.25 1.92893 1.92893 0.25 4 0.25Z" stroke="#D9D9D9" strokeWidth="0.5"/>
    <path d="M5.76563 2.1875H6.23438C6.27604 2.1875 6.29688 2.20833 6.29688 2.25V7.75C6.29688 7.79167 6.27604 7.8125 6.23438 7.8125H5.76563C5.72396 7.8125 5.70313 7.79167 5.70313 7.75V2.25C5.70313 2.20833 5.72396 2.1875 5.76563 2.1875Z" fill="black" fillOpacity="0.88"/>
    <path d="M3.375 4.70313H8.625C8.66667 4.70313 8.6875 4.72396 8.6875 4.76563V5.23438C8.6875 5.27604 8.66667 5.29688 8.625 5.29688H3.375C3.33333 5.29688 3.3125 5.27604 3.3125 5.23438V4.76563C3.3125 4.72396 3.33333 4.70313 3.375 4.70313Z" fill="black" fillOpacity="0.88"/>
  </svg>
);

const CollapseIcon = () => (
  <svg width="12" height="10" viewBox="0 0 12 10" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 0.25H8C10.0711 0.25 11.75 1.92893 11.75 4V6C11.75 8.07107 10.0711 9.75 8 9.75H4C1.92893 9.75 0.25 8.07107 0.25 6V4C0.25 1.92893 1.92893 0.25 4 0.25Z" fill="white"/>
    <path d="M4 0.25H8C10.0711 0.25 11.75 1.92893 11.75 4V6C11.75 8.07107 10.0711 9.75 8 9.75H4C1.92893 9.75 0.25 8.07107 0.25 6V4C0.25 1.92893 1.92893 0.25 4 0.25Z" stroke="#D9D9D9" strokeWidth="0.5"/>
    <path d="M4 5H8.2" stroke="black" strokeWidth="0.6" strokeLinecap="square"/>
  </svg>
);

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** Resalta las coincidencias de `query` en `text`. La coincidencia "actual" va en verde fuerte. */
function highlight(text: string, query: string, current: boolean) {
  const q = query.trim();
  if (!q) return text;
  const parts = text.split(new RegExp(`(${escapeRegExp(q)})`, "gi"));
  return parts.map((part, i) =>
    part.toLowerCase() === q.toLowerCase() ? (
      <mark
        key={i}
        style={{
          background: current ? "#00733B" : "#b7f5d2",
          color: current ? "#fff" : "inherit",
          padding: 0,
          borderRadius: 2,
        }}
      >
        {part}
      </mark>
    ) : (
      part
    ),
  );
}

/** URL en texto plano: con protocolo, o empezando en `www.` (WhatsApp manda las dos formas). */
const URL_RE = /(?:https?:\/\/|www\.)[^\s<]+/gi;
/** Puntuación que suele quedar pegada al final de un link dentro de una oración y no es parte de él. */
const URL_TRAILING = /[.,;:!?)\]}"'…]+$/;

/**
 * Renderiza el texto de un mensaje convirtiendo las URLs en links clickeables, sin perder el
 * resaltado del buscador del chat: los tramos de texto y el propio link pasan por `highlight`.
 * Se usa `noreferrer noopener` porque el contenido lo escribe un tercero (el contacto).
 */
function renderMessageText(text: string, query: string, current: boolean): ReactNode {
  const piece = (s: string, key: string): ReactNode =>
    query.trim() ? <span key={key}>{highlight(s, query, current)}</span> : s;

  const parts: ReactNode[] = [];
  let last = 0;
  let i = 0;
  for (const m of text.matchAll(URL_RE)) {
    const start = m.index ?? 0;
    const trail = m[0].match(URL_TRAILING)?.[0] ?? "";
    const url = trail ? m[0].slice(0, -trail.length) : m[0];
    if (!url) continue;
    if (start > last) parts.push(piece(text.slice(last, start), `t${i}`));
    parts.push(
      <a
        key={`u${i}`}
        href={url.startsWith("http") ? url : `https://${url}`}
        target="_blank"
        rel="noreferrer noopener"
        style={{ wordBreak: "break-all" }}
      >
        {piece(url, `ul${i}`)}
      </a>,
    );
    // El `trail` no se consume: vuelve como texto en el tramo siguiente.
    last = start + url.length;
    i++;
  }
  if (last < text.length) parts.push(piece(text.slice(last), `t${i}`));
  return parts.length ? parts : text;
}

/** Render de multimedia del mensaje según el content_type. La URL lleva el token del operador. */
function renderMedia(contentType: string | undefined, url: string) {
  const src = mediaSrc(url);
  if (contentType === "image")
    return (
      // `loading="lazy"`: el navegador baja la imagen recién cuando está por entrar en pantalla.
      // En un chat largo eso evita pedir de golpe todas las imágenes del historial — ahorra datos
      // del operador y egress de S3, sin cambiar lo que ve (la imagen sigue inline).
      <img
        src={src}
        alt=""
        loading="lazy"
        decoding="async"
        style={{ maxWidth: 240, maxHeight: 240, borderRadius: 8, display: "block", cursor: "pointer" }}
        onClick={() => window.open(src, "_blank")}
      />
    );
  if (contentType === "sticker")
    return (
      // Un sticker no es una imagen chica: es su propio gesto. Va **sin fondo de burbuja**, a ~120px
      // y sin lightbox — abrir un sticker en una pestaña no le sirve a nadie.
      //
      // Los ANIMADOS no necesitan nada especial: son `.webp` con animación y un `<img>` los anima
      // solo. Sin librería, sin convertir a GIF, sin player.
      <img
        src={src}
        alt="sticker"
        loading="lazy"
        decoding="async"
        style={{ width: 120, height: 120, objectFit: "contain", display: "block" }}
      />
    );
  if (contentType === "video")
    // `preload="none"`: sin esto el navegador baja metadata (y a veces los primeros bytes) de CADA
    // video del chat al abrirlo. Con esto no se toca el archivo hasta que le dan play.
    return (
      <video
        src={src}
        controls
        preload="none"
        style={{ maxWidth: 240, borderRadius: 8, display: "block" }}
      />
    );
  if (contentType === "audio")
    return <audio src={src} controls preload="metadata" style={{ display: "block", width: 240, maxWidth: "100%" }} />;
  return (
    <a href={src} target="_blank" rel="noreferrer">
      <PaperClipOutlined /> Descargar archivo
    </a>
  );
}

// ── Notas internas (OpenSpec 002) — fondo amarillo, el contacto nunca las ve ──
const NOTE_BG = "#FEF9E6";
const NOTE_BORDER = "#F5E6A8";
/** La nota de un asistente, un tono más clara: se distingue de la de una persona (#633). */
const AGENT_NOTE_BG = "#FFFDF4";
const AGENT_NOTE_BORDER = "#FAF0C8";
const MENTION_RE = /@\[([^\]]+)\]\((\d+)\)/g;

// ── El azul de Meta (#513), el del ícono del canal: lo que avisa una regla de Meta ──
const META_AZUL = "#0064E1";
const META_AZUL_FONDO = "#EEF5FF";
const META_AZUL_BORDE = "#B3D4FE";

/**
 * Render del contenido de una nota: los marcadores `@[Nombre](id)` salen como chip.
 *
 * Mismo chip que el Historial —verde de marca sobre ese verde al 10%— importado de
 * `mention-style` en vez de copiado: duplicar el color era garantía de que en algún momento uno de
 * los dos quedara distinto. Acá venía sin fondo, sólo con el texto en verde.
 */
/**
 * El texto de una nota con sus links en verde y clickeables, en una pestaña nueva. El número de un
 * «presupuesto #N» abre el presupuesto en el panel (D59).
 */
function conLinks(texto: string, key: string): ReactNode[] {
  return partirNota(texto).map((t, i) =>
    t.tipo === "presupuesto" ? (
      <a
        key={`${key}-${i}`}
        href={`/quotes/${t.quoteId}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: BRAND, textDecoration: "underline" }}
      >
        {t.texto}
      </a>
    ) : t.tipo === "link" ? (
      <a
        key={`${key}-${i}`}
        href={t.url}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: BRAND, textDecoration: "underline", wordBreak: "break-all" }}
      >
        {t.url}
      </a>
    ) : (
      t.texto
    ),
  );
}

function renderNoteContent(content: string) {
  const parts: ReactNode[] = [];
  let last = 0;
  for (const m of content.matchAll(MENTION_RE)) {
    if (m.index! > last) parts.push(...conLinks(content.slice(last, m.index), `t${last}`));
    parts.push(
      <span
        key={`${m.index}`}
        style={{
          color: BRAND,
          backgroundColor: MENTION_BG,
          fontStyle: "italic",
          fontWeight: 500,
          padding: "1px 4px",
          borderRadius: 4,
          whiteSpace: "nowrap",
        }}
      >
        @{m[1]}
      </span>,
    );
    last = m.index! + m[0].length;
  }
  if (last < content.length) parts.push(...conLinks(content.slice(last), `t${last}`));
  return parts.length ? parts : content;
}

/** El valor del radio «Otra respuesta»: no es un id de opción (esos los pone el servicio, «1», «2»…). */
const OTRA_RESPUESTA = "__otra__";

/**
 * Las opciones de una nota del asistente (D49): un radio por opción, con su detalle al lado, y
 * «Confirmar». Si ninguna sirve, «Otra respuesta» abre un campo para escribir la propia. Respondida,
 * queda marcada la elegida —o el texto— y quién respondió; nadie más puede cambiarla.
 */
function NoteChoicesBlock({
  note,
  enabled,
  onChoose,
}: {
  note: NoteView;
  enabled: boolean;
  onChoose: (note: NoteView, eleccion: EleccionDeOpcion) => Promise<void>;
}) {
  const [selected, setSelected] = useState<string | null>(null);
  const [texto, setTexto] = useState("");
  const [saving, setSaving] = useState(false);
  const answer = note.choices?.answer ?? null;
  const otra = selected === OTRA_RESPUESTA;
  const eleccion: EleccionDeOpcion | null = !selected
    ? null
    : otra
      ? texto.trim()
        ? { text: texto.trim() }
        : null
      : { optionId: selected };
  const confirmar = async () => {
    if (!eleccion) return;
    setSaving(true);
    try {
      await onChoose(note, eleccion);
    } finally {
      setSaving(false);
    }
  };
  return (
    <div style={{ marginTop: 8 }}>
      <Radio.Group
        value={answer ? (answer.custom ? OTRA_RESPUESTA : answer.optionId) : selected}
        disabled={!enabled || saving}
        onChange={(e) => setSelected(e.target.value)}
        style={{ display: "flex", flexDirection: "column", gap: 4 }}
      >
        {(note.choices?.options ?? []).map((o) => (
          <Radio key={o.id} value={o.id}>
            <span style={{ fontSize: 13 }}>{o.label}</span>
            {o.detail && <Text style={{ fontSize: 12, color: "#8C8C8C", marginLeft: 6 }}>{o.detail}</Text>}
          </Radio>
        ))}
        {/* Respondida con una opción, «Otra respuesta» no aporta nada: no se muestra. */}
        {(!answer || answer.custom) && (
          <Radio value={OTRA_RESPUESTA}>
            <span style={{ fontSize: 13 }}>Otra respuesta</span>
          </Radio>
        )}
      </Radio.Group>
      {!answer && enabled && otra && (
        <Input.TextArea
          autoFocus
          autoSize={{ minRows: 1, maxRows: 4 }}
          maxLength={RESPUESTA_LIBRE_MAX}
          placeholder="Escribí tu respuesta (Enter confirma)"
          value={texto}
          disabled={saving}
          onChange={(e) => setTexto(e.target.value)}
          onPressEnter={(e) => {
            if (!e.shiftKey) {
              e.preventDefault();
              void confirmar();
            }
          }}
          style={{ marginTop: 6, fontSize: 13 }}
        />
      )}
      {/* Respondida, no se repite quién eligió: lo dice la nota del operador que sale al elegir (#633). */}
      {!answer && enabled && (
        <Button
          size="small"
          type="primary"
          style={{ marginTop: 8 }}
          disabled={!eleccion}
          loading={saving}
          onClick={() => void confirmar()}
        >
          Confirmar
        </Button>
      )}
    </div>
  );
}

/**
 * La nota a la que responde otra (punto 4), como el reply de WhatsApp: una franja con el autor y el
 * comienzo del texto. En la nota lleva a la original; en el compose lleva la X para cancelar.
 */
function CitaDeNota({
  replyTo,
  onClick,
  onCancel,
}: {
  replyTo: NoteReplyTo;
  onClick?: (replyTo: NoteReplyTo) => void;
  onCancel?: () => void;
}) {
  return (
    <Flex
      align="center"
      gap={8}
      onClick={onClick ? () => onClick(replyTo) : undefined}
      style={{
        marginTop: 4,
        padding: "4px 8px",
        borderLeft: "3px solid #00733B",
        background: "rgba(0, 115, 59, 0.06)",
        borderRadius: 4,
        cursor: onClick ? "pointer" : undefined,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <Text style={{ display: "block", color: "#00733B", fontWeight: 600, fontSize: 12 }}>
          {replyTo.authorName}
        </Text>
        <Text ellipsis style={{ display: "block", fontSize: 12, color: "#595959" }}>
          {replyTo.excerpt}
        </Text>
      </div>
      {onCancel ? (
        <Button size="small" type="text" aria-label="Cancelar respuesta" icon={<CloseOutlined />} onClick={onCancel} />
      ) : null}
    </Flex>
  );
}

/** Bloque de nota (general inline o anidada en la burbuja), estilo Figma. */
function NoteBlock({
  note,
  mine,
  nested,
  onDelete,
  canChoose = false,
  onChoose,
  sendLink,
  actividad,
  onReply,
  onCancelarConsulta,
  onJump,
  tareas,
  onCancelarTarea,
  onCambiarHoraTarea,
  delAsistente = false,
  sinCita = false,
}: {
  note: NoteView;
  mine: boolean;
  nested?: boolean;
  /** La escribió un asistente: lleva su ícono en vez del de persona (#633). */
  delAsistente?: boolean;
  /** Dentro del hilo de un mensaje la nota citada está justo arriba: la cita sobra (#633). */
  sinCita?: boolean;
  /** D65: las tareas programadas que confirma esta nota del asistente. */
  tareas?: NoteTask[];
  onCancelarTarea?: (taskId: string) => Promise<void>;
  onCambiarHoraTarea?: (taskId: string, runAt: string) => Promise<boolean>;
  /** Punto 4: «Agregar nota» en el menú (antes «Responder», #633). Sin esto —el operador no es Editor— no aparece. */
  onReply?: (note: NoteView) => void;
  /** D67: la nota propia que el asistente todavía no respondió. Sin esto no aparece. */
  onCancelarConsulta?: (note: NoteView) => void;
  /** Punto 4: la cita de una respuesta lleva a la nota original. */
  onJump?: (replyTo: NoteReplyTo) => void;
  /** D56: lo que el asistente está haciendo por esta nota, si lo arrobó. */
  actividad?: ActividadDelAsistente;
  onDelete: (note: NoteView) => void;
  /** D49: si este operador puede elegir una opción de la nota. */
  canChoose?: boolean;
  onChoose?: (note: NoteView, eleccion: EleccionDeOpcion) => Promise<void>;
  /**
   * El link de presupuesto de la nota se manda al cliente desde acá. Sin esto —el operador no puede
   * escribir en el canal— el botón no aparece. `sent`: ya salió un mensaje con ese link.
   */
  sendLink?: { onSend: (link: string, texto: string) => Promise<void>; sent: (link: string) => boolean };
}) {
  const link = sendLink ? linkDePresupuesto(note.content) : null;
  /** Lo que se está confirmando desde el menú: borrar la nota o cancelar la consulta (D67). */
  const [confirmar, setConfirmar] = useState<"borrar" | "cancelar" | null>(null);
  const acciones = [
    ...(onReply ? [{ key: "reply", label: "Agregar nota" }] : []),
    ...(onCancelarConsulta ? [{ key: "cancel-request", label: "Cancelar consulta" }] : []),
    ...(mine ? [{ key: "delete", label: "Eliminar", danger: true }] : []),
  ];
  return (
    <>
      <div
        style={{
          background: delAsistente ? AGENT_NOTE_BG : NOTE_BG,
          border: `1px solid ${delAsistente ? AGENT_NOTE_BORDER : NOTE_BORDER}`,
          borderRadius: 8,
          padding: "8px 12px",
          marginTop: nested ? 8 : 0,
        }}
      >
        <Flex align="center" justify="space-between" gap={8}>
          <Space size={6}>
            {delAsistente ? (
              <AgentIcon style={{ color: "#00733B", fontSize: 12 }} />
            ) : (
              <UserOutlined style={{ color: "#00733B", fontSize: 12 }} />
            )}
            <Text style={{ color: "#00733B", fontWeight: 600, fontSize: 13 }}>{note.authorName}</Text>
          </Space>
          <Space size={4}>
            <Text style={{ fontSize: 11, color: "#8C8C8C" }}>
              {note.createdAt ? dayjs(note.createdAt).format("HH:mm") : ""}
            </Text>
            {acciones.length > 0 && (
              // El borrado y la cancelación se confirman; el Popconfirm se abre desde el menú.
              <Popconfirm
                title={confirmar === "cancelar" ? "¿Cancelar la consulta al asistente?" : "¿Eliminar la nota?"}
                description={confirmar === "cancelar" ? "No va a responder esta nota." : undefined}
                icon={null}
                okText={confirmar === "cancelar" ? "Sí, cancelar" : "Sí, eliminar"}
                cancelText={confirmar === "cancelar" ? "No" : "Cancelar"}
                // Si el asistente respondió mientras se confirmaba, ya no hay consulta que cancelar.
                open={confirmar === "borrar" || (confirmar === "cancelar" && !!onCancelarConsulta)}
                onOpenChange={(v) => !v && setConfirmar(null)}
                onConfirm={() => {
                  const accion = confirmar;
                  setConfirmar(null);
                  if (accion === "cancelar") onCancelarConsulta?.(note);
                  else onDelete(note);
                }}
              >
                <Dropdown
                  trigger={["click"]}
                  menu={{
                    items: acciones,
                    onClick: ({ key }) => {
                      if (key === "reply") onReply?.(note);
                      if (key === "cancel-request") setConfirmar("cancelar");
                      if (key === "delete") setConfirmar("borrar");
                    },
                  }}
                >
                  <Button
                    size="small"
                    type="text"
                    aria-label="Acciones de la nota"
                    icon={<MoreOutlined />}
                    style={{ height: 18, width: 18 }}
                  />
                </Dropdown>
              </Popconfirm>
            )}
          </Space>
        </Flex>
        {note.replyTo && !sinCita ? <CitaDeNota replyTo={note.replyTo} onClick={onJump} /> : null}
        <div style={{ fontSize: 13, color: "#262626", marginTop: 2, whiteSpace: "pre-wrap" }}>
          {renderNoteContent(note.content)}
        </div>
        {note.choices?.options?.length && onChoose ? (
          <NoteChoicesBlock note={note} enabled={canChoose} onChoose={onChoose} />
        ) : null}
        {(tareas ?? []).map((t) => (
          <TareaDeLaNota
            key={t.taskId}
            tarea={t}
            onCancelar={onCancelarTarea}
            onCambiarHora={onCambiarHoraTarea}
          />
        ))}
        {link && sendLink ? <SendLinkButton link={link} sent={sendLink.sent(link)} onSend={sendLink.onSend} /> : null}
      </div>
      {/* Mientras trabaja, el asistente no escribe dentro de la nota del operador: su nota va aparte,
          debajo, en el lugar donde después llega la respuesta (#633). */}
      {actividad ? <NotaDelAsistentePensando actividad={actividad} /> : null}
    </>
  );
}

/** La nota del asistente que todavía no tiene respuesta: su nombre y lo que está haciendo (D56, #633). */
function NotaDelAsistentePensando({ actividad }: { actividad: ActividadDelAsistente }) {
  return (
    <div
      style={{
        background: AGENT_NOTE_BG,
        border: `1px solid ${AGENT_NOTE_BORDER}`,
        borderRadius: 8,
        padding: "8px 12px",
        marginTop: 8,
      }}
    >
      <Space size={6}>
        <AgentIcon style={{ color: "#00733B", fontSize: 12 }} />
        <Text style={{ color: "#00733B", fontWeight: 600, fontSize: 13 }}>
          {actividad.agentName?.trim() || "Asistente"}
        </Text>
      </Space>
      <ActividadDelAsistenteRow actividad={actividad} />
    </div>
  );
}

/**
 * «Agregar nota» en el lugar, como en el Historial (#633): el campo con @ y ➤. Se envía con el botón
 * (Enter se lo queda antd para elegir la mención) y Esc cierra.
 */
function NotaEnLinea({
  value,
  onChange,
  onSend,
  onCancel,
  sending,
  options,
  names,
  placeholder = "Nota sobre este mensaje — el contacto no la ve (@ para mencionar)",
}: {
  value: string;
  placeholder?: string;
  onChange: (v: string) => void;
  onSend: () => void;
  onCancel: () => void;
  sending: boolean;
  options: { value: string; label: ReactNode; key: string }[];
  names: string[];
}) {
  return (
    <Flex
      gap={8}
      align="flex-start"
      style={{ marginTop: 8, background: "#fff", border: "1px solid #d9d9d9", borderRadius: 6, padding: 8 }}
    >
      <MentionsInput
        autoFocus
        value={value}
        onChange={onChange}
        options={options}
        names={names}
        placeholder={placeholder}
        autoSize={{ minRows: 2, maxRows: 5 }}
        variant="borderless"
        style={{ flex: 1 }}
        onKeyDown={(e) => {
          if (e.key === "Escape") onCancel();
        }}
      />
      <Button
        type="text"
        icon={<SendOutlined />}
        loading={sending}
        disabled={!value.trim()}
        onClick={onSend}
        title="Agregar nota"
      />
    </Flex>
  );
}

/** «Enviar al cliente»: manda el link de presupuesto de la nota con el texto de siempre, previa confirmación. */
function SendLinkButton({
  link,
  sent,
  onSend,
}: {
  link: string;
  sent: boolean;
  onSend: (link: string, texto: string) => Promise<void>;
}) {
  const [sending, setSending] = useState(false);
  // El lápiz deja corregir lo que redactó el asistente antes de que le llegue al cliente (#633).
  const [texto, setTexto] = useState(() => quoteLinkMessage(link));
  const [editando, setEditando] = useState(false);
  if (sent) {
    return (
      <Text style={{ display: "block", marginTop: 6, fontSize: 12, color: "#00733B" }}>
        <CheckOutlined /> Link enviado al cliente
      </Text>
    );
  }
  return (
    <Flex justify="flex-end" style={{ marginTop: 8 }}>
      <Popconfirm
        title="¿Mandarle el presupuesto al cliente?"
        description={
          editando ? (
            <Input.TextArea
              autoFocus
              autoSize={{ minRows: 3, maxRows: 8 }}
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              style={{ width: 320, fontSize: 13 }}
            />
          ) : (
            <Flex gap={4} align="flex-start">
              {/* El link es una sola «palabra» larga: sin `overflowWrap` no corta y se sale del popover. */}
              <div style={{ maxWidth: 320, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{texto}</div>
              <Button
                size="small"
                type="text"
                aria-label="Editar el mensaje"
                title="Editar el mensaje"
                icon={<EditOutlined />}
                onClick={() => setEditando(true)}
              />
            </Flex>
          )
        }
        okText="Enviar"
        cancelText="Cancelar"
        okButtonProps={{ disabled: !texto.trim() }}
        // Cada vez que se abre, arranca con el texto de siempre y sin editar.
        onOpenChange={(open) => {
          if (open) {
            setTexto(quoteLinkMessage(link));
            setEditando(false);
          }
        }}
        onConfirm={async () => {
          setSending(true);
          try {
            await onSend(link, texto.trim());
          } finally {
            setSending(false);
          }
        }}
      >
        <Button icon={<SendOutlined />} iconPosition="end" loading={sending}>
          Enviar al cliente
        </Button>
      </Popconfirm>
    </Flex>
  );
}

/** Label legible del evento del timeline (los scheduled_* incluyen la fecha programada). */
function eventLabel(eventType?: string, metadata?: { scheduledAt?: string } | null): string {
  const at = metadata?.scheduledAt ? dayjs(metadata.scheduledAt).format("DD/MM HH:mm") : "";
  switch (eventType) {
    case "scheduled_created":
      return `programó un mensaje para el ${at}`;
    case "scheduled_cancelled":
      return `canceló el mensaje programado del ${at}`;
    case "scheduled_sent":
      return `envió el mensaje programado del ${at}`;
    case "scheduled_failed":
      return `el mensaje programado del ${at} falló al enviarse`;
    default:
      return eventType ?? "";
  }
}

/**
 * Ticks de estado del saliente.
 *
 * `pending` = está en vuelo, todavía no lo tiene WhatsApp. Tiene caso propio **a propósito**: el
 * fallback de esta función es un tilde, así que sin esta línea un mensaje sin confirmar se vería
 * exactamente igual que uno entregado.
 *
 * - reloj  → enviando, WhatsApp no lo confirmó
 * - ✓      → WhatsApp lo aceptó
 * - ✓✓     → le llegó al destinatario
 * - ✓✓ azul→ lo leyó
 * - !      → falló
 */
function StatusTicks({ status }: { status?: string }) {
  // Un mensaje borrado para todos ya no tiene estado de entrega que mostrar.
  if (status === "deleted") return null;
  if (status === "pending")
    return <ClockCircleOutlined style={{ color: "#BFBFBF", fontSize: 10 }} />;
  if (status === "read") return <Text style={{ color: "#34B7F1", fontSize: 11 }}>✓✓</Text>;
  if (status === "delivered") return <Text style={{ color: "#8C8C8C", fontSize: 11 }}>✓✓</Text>;
  if (status === "failed") return <Text style={{ color: "#FF4D4F", fontSize: 11 }}>!</Text>;
  return <CheckOutlined style={{ color: "#8C8C8C", fontSize: 10 }} />;
}

/**
 * Cuánto se espera la respuesta del teléfono a un pedido de historial (015 F5) antes de avisar que
 * no llegó. WhatsApp no garantiza contestar estos pedidos, así que la espera tiene que tener fin.
 */
const HISTORY_WAIT_MS = 20_000;

export const Chat = ({
  conversation,
  socket,
  onOpenContact,
  onStatusChange,
  onRead,
  initialDraft,
  onDraftConsumed,
  focusComposerSignal,
  focusMessageId,
  onMessageFocused,
}: {
  conversation?: ConversationListItem;
  socket: Socket | null;
  onOpenContact: () => void;
  onStatusChange: () => void;
  /**
   * Se leyó esta conversación. Separado de `onStatusChange` a propósito: esto ocurre en CADA cambio
   * de conversación, y `onStatusChange` re-pide la bandeja entera (100 filas). Lo único que cambia
   * al marcar leído es el contador de una fila, así que se actualiza en memoria — sin request, sin
   * re-render de la lista completa, sin parpadeo.
   */
  onRead: (conversationId: string) => void;
  /** Texto preescrito que llega por deep-link (N6: compartir un presupuesto). */
  initialDraft?: string;
  onDraftConsumed?: () => void;
  /**
   * Contador: cada incremento pide el foco en el compose (P4). No alcanza con `autoFocus` porque
   * este componente NO remonta al cambiar de conversación —recibe `conversation` por prop— y
   * `autoFocus` sólo actúa al montar.
   */
  focusComposerSignal?: number;
  /**
   * Mensaje al que saltar al abrir (change `mensaje-con-link-al-presupuesto`, Fase 5): el id que
   * el historial guarda en `metadata.messageId`. Se busca en lo cargado y, si es más viejo, se
   * piden bloques anteriores a la base hasta encontrarlo, con tope.
   */
  focusMessageId?: string | null;
  onMessageFocused?: () => void;
}) => {
  const [expandedHeader, setExpandedHeader] = useState(false);
  /** El mensaje al que se acaba de saltar, resaltado unos segundos para que el ojo lo encuentre. */
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const focusBusyRef = useRef(false);
  const [rows, setRows] = useState<TimelineRow[]>([]);
  /** Los presupuestos que nombran las notas: si cambia, hay uno nuevo y se recargan contador y panel (#639). */
  const presupuestosNombrados = useMemo(() => presupuestosEnNotas(rows), [rows]);
  // D56: lo que el asistente está haciendo en esta conversación. La lista entera; vacía, terminó.
  const [actividad, setActividad] = useState<ActividadDelAsistente[]>([]);
  const actividadDeNota = useMemo(() => actividadPorNota(actividad), [actividad]);
  // La conversación abierta ahora: una respuesta que llega después de cambiar de chat se descarta.
  const convIdActual = useRef<string | undefined>(undefined);
  const recibirActividad = useCallback((r: { conversationId: string; items: ActividadDelAsistente[] }) => {
    if (r.conversationId === convIdActual.current) setActividad(r.items ?? []);
  }, []);
  const actividadDelPie = useMemo(() => actividadAlPie(actividad), [actividad]);
  // D65: las tareas programadas del asistente en esta conversación, para la franja y las notas.
  const [franja, despacharFranja] = useReducer(reducirFranja, FRANJA_VACIA);
  const tareasDeCadaNota = useMemo(() => tareasPorNota(franja.tareas), [franja.tareas]);
  /** Sólo la última lectura cuenta: dos `agent:tasks` seguidos pueden volver al revés. */
  const lecturaDeTareas = useRef(0);
  const releerTareas = useCallback((conversationId: string) => {
    const n = ++lecturaDeTareas.current;
    conversationsApi
      .listAgentTasks(conversationId)
      .then((tareas) => {
        if (n === lecturaDeTareas.current) despacharFranja({ tipo: "leidas", conversationId, tareas });
      })
      .catch(() => {});
  }, []);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [retrying, setRetrying] = useState<string | null>(null);
  const [headerContact, setHeaderContact] = useState<ContactMatch | null>(null);
  const [headerContactDetail, setHeaderContactDetail] = useState<ContactDetail | null>(null);
  const [quotesCount, setQuotesCount] = useState<number>(0);
  const [ordersCount, setOrdersCount] = useState<number>(0);
  const [contextPanel, setContextPanel] = useState<ContextPanelType>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [matchIndex, setMatchIndex] = useState(0);
  const [attachment, setAttachment] = useState<File | null>(null);
  const [recording, setRecording] = useState(false);
  const [recSecs, setRecSecs] = useState(0);
  // Notas internas (OpenSpec 002)
  const [me, setMe] = useState<number | null>(null);
  const [canEdit, setCanEdit] = useState(false);
  const [noteMode, setNoteMode] = useState(false);
  const [noteTarget, setNoteTarget] = useState<string | null>(null); // messageId del «Agregar nota»
  /** Punto 4: la nota a la que responde la que se está escribiendo. */
  const [replyTo, setReplyTo] = useState<NoteReplyTo | null>(null);
  const [noteText, setNoteText] = useState("");
  /** «Responder» a una nota que está en un mensaje: el campo se abre en esa burbuja (#633). */
  const [respuestaA, setRespuestaA] = useState<NoteReplyTo | null>(null);
  const [mentionables, setMentionables] = useState<{ userId: number; name: string }[]>([]);
  /** Los usuarios asistentes del store: sus notas y su fila del `@` llevan su ícono (#633). */
  const [agentIds, setAgentIds] = useState<Set<number>>(new Set());
  /** Las notas que llegaron sueltas y ya tienen su mensaje en pantalla: van en su burbuja (D67). */
  const hilos = useMemo(() => ubicarNotas(rows), [rows]);
  // Mensajes programados (OpenSpec 003)
  const [scheduledList, setScheduledList] = useState<ScheduledMessage[]>([]);
  const [programOpen, setProgramOpen] = useState(false);
  const [customDay, setCustomDay] = useState<Dayjs | null>(null);
  const [customTime, setCustomTime] = useState<Dayjs | null>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  // ── "Cargar más" hacia arriba (015 F5) ────────────────────────────────────────
  // Dos fuentes en cascada: primero se pagina NUESTRA DB (instantáneo), y cuando se agota se le
  // pide historial a WhatsApp (asíncrono, llega por WS). `historyExhausted` = tampoco queda allá.
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMoreDb, setHasMoreDb] = useState(true);
  const [historyExhausted, setHistoryExhausted] = useState(false);
  /** Pedido a WhatsApp esperando respuesta del teléfono, y si se venció esperándola. */
  const [waitingHistory, setWaitingHistory] = useState(false);
  const [historyError, setHistoryError] = useState(false);
  const historyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const topRef = useRef<HTMLDivElement>(null);
  // ── Cloud API (tarea 3.6) ─────────────────────────────────────────────────────
  /**
   * D27: hasta cuándo se le puede escribir al contacto. Llega en la conversación y, cuando el contacto
   * escribe, por `conversation:update`; un rechazo del envío la cierra.
   */
  const [windowExpiresAt, setWindowExpiresAt] = useState<string | null>(null);
  /** D31: el envío por la Cloud API está pausado en este ambiente. Se sabe al intentar mandar. */
  const [sendingPaused, setSendingPaused] = useState(false);
  /** «Enviar plantilla» (D13): lo único que sale con la ventana cerrada. */
  const [templatePicker, setTemplatePicker] = useState(false);
  /** D2: Meta rechazó un mensaje de este número por el método de pago de la cuenta. */
  const [paymentIssue, setPaymentIssue] = useState(false);
  /** La ventana cierra con el tiempo, no con un evento: esto la vuelve a mirar. */
  const [now, setNow] = useState(() => new Date());
  /** Alto del scroll antes de prependear, para no perder la posición de lectura. */
  const prependHeightRef = useRef<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const composerRef = useRef<TextAreaRef>(null);
  /** Último contacto vinculado que ya atendimos: el aviso llega por dos rooms y vendría duplicado. */
  const linkedContactRef = useRef<number | null>(null);
  /** Conversación cuya cabecera se está resolviendo: descarta respuestas que llegan tarde. */
  const headerConvRef = useRef<string | undefined>(undefined);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const recTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const msgRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const sellersScrollRef = useRef<HTMLDivElement>(null);
  const firstSellerRowRef = useRef<HTMLDivElement>(null);
  const convId = conversation?.conversation_id;

  // Una respuesta a medio escribir es de su conversación: al cambiar de chat no se lleva la cita.
  useEffect(() => {
    setReplyTo(null);
  }, [convId]);

  /** Punto 4: «Responder» en una nota deja el compose en modo nota, con la cita arriba. */
  function responderNota(note: NoteView) {
    // Una nota que se ve dentro de un mensaje se responde ahí, no en el compose (#633). Dónde queda
    // la respuesta lo decide el servidor (D67): ahí mismo, o general si ya se le escribió al cliente.
    const enMensaje = mensajeDondeResponder(note, hilos.mensajes);
    if (enMensaje) {
      setNoteTarget(enMensaje);
      setNoteText("");
      setRespuestaA(citaDeNota(note));
      return;
    }
    setReplyTo(citaDeNota(note));
    setNoteMode(true);
    setAttachment(null);
  }

  /** La cita de una respuesta lleva a la original, si está cargada en pantalla. */
  function irACita(r: NoteReplyTo) {
    const el = msgRefs.current[destinoDeLaCita(r)];
    if (el) el.scrollIntoView({ block: "center", behavior: "smooth" });
    else antdMessage.info("La nota original está más arriba: cargá más mensajes para verla");
  }

  /** D65: una tarea de la franja lleva a la nota del asistente que la confirma, como la cita. */
  function irATarea(t: TaskView) {
    const destino = destinoDeLaTarea(rows, t);
    const el = destino ? msgRefs.current[destino] : null;
    if (el) el.scrollIntoView({ block: "center", behavior: "smooth" });
    else antdMessage.info("La nota de la tarea está más arriba: cargá más mensajes para verla");
  }
  const cloud = isCloudApi(conversation?.channel_type);
  /** Cloud API: no hay historial a pedido; lo de antes del onboarding lo trae el sync (D23). */
  const sinHistorial = capabilitiesOf(conversation?.channel_type)?.history === false;

  /**
   * La ventana de la conversación que se abrió. La de la bandeja puede estar vieja —el contacto pudo
   * escribir desde que se pidió la lista, y `inbox:update` no la trae—, así que en un canal de la Cloud
   * API se pide la conversación, que la trae de la base. Gana la más reciente: la respuesta no pisa un
   * `conversation:update` que haya llegado mientras tanto.
   */
  useEffect(() => {
    setWindowExpiresAt(conversation?.service_window_expires_at ?? null);
    if (!convId || !cloud) return;
    let vigente = true;
    conversationsApi
      .getConversation(convId)
      .then((c) => {
        if (vigente) setWindowExpiresAt((prev) => ventanaMasReciente(prev, c.service_window_expires_at));
      })
      .catch(() => {});
    return () => {
      vigente = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convId, cloud]);
  // Un refetch de la bandeja con la misma conversación abierta.
  useEffect(() => {
    setWindowExpiresAt((prev) => ventanaMasReciente(prev, conversation?.service_window_expires_at));
  }, [conversation?.service_window_expires_at]);
  useEffect(() => {
    setSendingPaused(false);
    setPaymentIssue(false);
    setTemplatePicker(false);
  }, [convId]);
  useEffect(() => {
    if (!cloud) return;
    const t = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(t);
  }, [cloud]);

  /** Lo que un error del envío, o un acuse fallido, le cambia al chat. */
  const aplicarEfecto = (efecto: EfectoDelError) => {
    if (efecto.ventanaCerrada) {
      setWindowExpiresAt(efecto.ventanaCerrada.expiresAt);
      setNow(new Date());
    }
    if (efecto.pausado) setSendingPaused(true);
    if (efecto.pago) setPaymentIssue(true);
    if (efecto.refrescarCanal) onStatusChange();
  };
  /** El error de un envío: aplica su efecto y avisa, como aviso si es una regla de WhatsApp. */
  const avisarErrorDeEnvio = (e: any, prefijo: string) => {
    const efecto = efectoDelError(e);
    aplicarEfecto(efecto);
    const texto = e?.response?.data?.message ?? e?.message ?? e;
    if (efecto.esAviso) antdMessage.warning(texto);
    else antdMessage.error(`${prefijo}: ${texto}`);
  };

  useEffect(() => {
    if (expandedHeader) {
      requestAnimationFrame(() => {
        const container = sellersScrollRef.current;
        const target = firstSellerRowRef.current;
        if (container && target)
          container.scrollTop = target.offsetTop;
      });
    } else if (sellersScrollRef.current) {
      sellersScrollRef.current.scrollTop = 0;
    }
  }, [expandedHeader]);

  /**
   * Texto que llegó por deep-link (N6). Se aplica **una sola vez** y se avisa al padre para que lo
   * limpie: si no, volver a esta conversación pisaría lo que el operador esté escribiendo. Y no se
   * sobreescribe un compose que ya tiene algo, por la misma razón.
   */
  useEffect(() => {
    if (!initialDraft || !convId) return;
    setText((prev) => (prev.trim() ? prev : initialDraft));
    onDraftConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDraft, convId]);

  /**
   * Foco en el compose cuando el operador llega con intención de escribir (P4): alta desde "Nuevo
   * mensaje" o deep-link de compartir presupuesto. Abrir una conversación desde el listado NO enfoca
   * —ahí lo normal es leer—.
   *
   * El `setTimeout(0)` espera a que el textarea exista: si el canal está caído se renderiza el
   * compose de nota en su lugar (que ya trae su propio `autoFocus`), y si la conversación se acaba
   * de crear, el mount llega en el render siguiente a la señal.
   *
   * `cursor: "end"` deja el caret al final del texto preescrito, listo para enviar o seguir tipeando.
   */
  useEffect(() => {
    if (!focusComposerSignal) return;
    const t = setTimeout(() => composerRef.current?.focus({ cursor: "end" }), 0);
    return () => clearTimeout(t);
  }, [focusComposerSignal]);

  // Búsqueda en chat (client-side sobre los mensajes cargados).
  const matches = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return [] as string[];
    return rows
      .filter((r) => r.kind === "message" && (r.payload.content ?? "").toLowerCase().includes(q))
      .map((r) => r.id);
  }, [rows, searchText]);
  const currentMatchId = matches[matchIndex];

  useEffect(() => setMatchIndex(0), [searchText]);
  useEffect(() => {
    if (currentMatchId) msgRefs.current[currentMatchId]?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [currentMatchId]);

  /**
   * Saltar al mensaje del deep-link (Fase 5 de `mensaje-con-link-al-presupuesto`).
   *
   * Corre cuando la conversación ya cargó su primera página. Si el mensaje no está, pide bloques
   * anteriores a la base —de a `FOCUS_PAGE`, hasta `FOCUS_MAX_PAGES`— y los agrega por
   * `prependRows`, que es el mismo camino de "cargar más". Lo que está más atrás que la base sólo
   * lo tiene el teléfono, y eso no se pide solo: se avisa y queda el botón de siempre.
   */
  useEffect(() => {
    // `rows.length === 0` es la guarda que importa: `loading` arranca en `false` y con el timeline
    // cacheado nunca pasa a `true`, así que sola no dice si la primera página ya está.
    if (!focusMessageId || !convId || loading || rows.length === 0 || focusBusyRef.current) return;
    focusBusyRef.current = true;
    let vigente = true;
    const esElBuscado = (r: TimelineRow) =>
      r.kind === "message" && (r.id === focusMessageId || r.payload.messageId === focusMessageId);
    (async () => {
      let filas = rows;
      let masEnDb = hasMoreDb;
      for (let pagina = 0; !filas.some(esElBuscado) && masEnDb && pagina < FOCUS_MAX_PAGES; pagina++) {
        const oldest = filas[0]?.occurredAt;
        const page = await conversationsApi.getTimeline(convId, oldest, FOCUS_PAGE);
        if (!vigente) return;
        if (!page.length) {
          masEnDb = false;
          break;
        }
        const nuevas = page.slice().reverse();
        prependRows(nuevas);
        filas = [...nuevas, ...filas];
        if (page.length < FOCUS_PAGE) masEnDb = false;
      }
      if (!vigente) return;
      if (!masEnDb) setHasMoreDb(false);
      const hit = filas.find(esElBuscado);
      if (hit) {
        setFocusedId(hit.id);
        // El scroll va después del render de las filas nuevas; `prependRows` acomoda el alto.
        setTimeout(() => msgRefs.current[hit.id]?.scrollIntoView({ block: "center", behavior: "smooth" }), 80);
        setTimeout(() => setFocusedId((actual) => (actual === hit.id ? null : actual)), 4000);
      } else {
        antdMessage.info("El mensaje es más viejo que el historial cargado: podés seguir con «Cargar más».");
      }
    })()
      .catch(() => {
        // Un error de red no tiene que dejar la conversación sin abrir: el mensaje ya se ve igual.
      })
      .finally(() => {
        focusBusyRef.current = false;
        if (vigente) onMessageFocused?.();
      });
    return () => {
      vigente = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusMessageId, convId, loading, rows.length > 0]);

  const gotoMatch = (dir: 1 | -1) =>
    setMatchIndex((i) => (matches.length ? (i + dir + matches.length) % matches.length : 0));
  const closeSearch = () => {
    setSearchOpen(false);
    setSearchText("");
  };

  const connected = conversation?.channel_connection === "connected" && conversation?.channel_active;
  /**
   * #177: WhatsApp restringió la CUENTA de este número (`RESTRICT_ALL_COMPANIONS`). Es distinto de
   * un canal caído: la sesión está sana y el canal puede figurar `connected`, pero **todo lo que se
   * mande se rechaza**. Antes eso era invisible — el ack de error llega después del `201`, así que
   * el panel mostraba un ✓ y el operador se enteraba por el cliente. Y re-vincular con QR no lo
   * arregla: la restricción es de la cuenta, no de la sesión.
   */
  // Sin fecha también restringe: la detección por racha de timeouts no trae vencimiento, así que
  // el vencimiento no se parsea acá — lo formatea `textoVencimiento`, que tolera el null.
  const restricted = estaRestringido({
    restrictedUntil: conversation?.channel_restricted_until,
    restrictionType: conversation?.channel_restriction_type,
  });
  /**
   * El motivo por el que no sale nada desde el panel, o `null`: el canal caído, la cuenta restringida,
   * y en la Cloud API el envío pausado (D31) o la ventana de 24 h cerrada (D27).
   */
  const bloqueo = motivoDeBloqueo({
    connected: Boolean(connected),
    restricted,
    paused: cloud && sendingPaused,
    windowOpen: ventanaAbierta(conversation?.channel_type, windowExpiresAt, now),
  });
  /**
   * Canal caído (o cuenta restringida): no se pueden mandar mensajes, pero la **nota interna sí** —
   * es local, no pasa por WhatsApp, y el backend nunca la bloqueó (`createNote` solo pide ser
   * Editor). Antes el compose entero desaparecía y con él la única forma de escribir una nota, justo
   * en el momento en que más sirve: dejar registro de que algo no se pudo responder.
   */
  const noteOnly = bloqueo !== null && canEdit;
  /** El último saliente fue una plantilla: lo que se espera es la respuesta del contacto. */
  const plantillaEnviada = esDeTemplate(
    [...rows].reverse().find((r) => r.kind === "message" && r.payload.senderType === "operator")?.payload,
  );
  /** Modo nota efectivo: el que eligió el operador, o el forzado (canal caído o cuenta restringida). */
  const composingNote = noteMode || noteOnly;
  const closed = conversation?.status === "closed";
  /**
   * La plantilla (#519) se ofrece en la caja en todo canal Cloud API, con la ventana abierta o cerrada.
   * No con el envío pausado o la cuenta restringida: ahí tampoco sale una plantilla.
   */
  const puedeMandarPlantilla = cloud && canEdit && (bloqueo === null || bloqueo === "ventana");

  // Quién soy (para "solo el autor elimina su nota").
  useEffect(() => {
    conversationsApi.me().then((s) => setMe(s.userId)).catch(() => {});
  }, []);

  // Nivel efectivo sobre el canal: gatea crear notas (Viewer = read-only).
  // Si soy Editor, precarga los candidatos al autocomplete @ (mentionables).
  useEffect(() => {
    setCanEdit(false);
    setNoteMode(false);
    setMentionables([]);
    if (!conversation?.channel_id) return;
    const channelId = conversation.channel_id;
    let active = true;
    conversationsApi
      .myChannelLevel(channelId)
      .then((r) => {
        if (!active) return;
        setCanEdit(r.level === "editor");
        if (r.level === "editor") {
          conversationsApi
            .mentionables(channelId)
            .then((list) => {
              if (active) setMentionables(list);
            })
            .catch(() => {});
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [conversation?.channel_id]);

  // Quiénes son asistentes, para su ícono en las notas y en el `@` (#633). Los del store (D64: se
  // arroba a cualquiera) piden ver Operadores del Store; sin eso, al menos el que atiende el canal.
  useEffect(() => {
    setAgentIds(new Set());
    if (!conversation?.channel_id) return;
    let active = true;
    const sumar = (ids: number[]) => {
      if (active) setAgentIds((prev) => new Set([...prev, ...ids]));
    };
    conversationsApi
      .getChannelAgent(conversation.channel_id)
      .then((a) => a && sumar([a.userId]))
      .catch(() => {});
    if (conversation.store_code)
      agentApi
        .asistentes(conversation.store_code)
        .then((list) => sumar(list.map((a) => a.userId)))
        .catch(() => {});
    return () => {
      active = false;
    };
  }, [conversation?.channel_id, conversation?.store_code]);

  // Opciones del autocomplete @: al elegir, Mentions inserta `@` + value → nuestro marcador @[Nombre](id).
  /**
   * Operadores que aparecen como remitentes en este chat, resueltos por id (Q2). Se usa para mostrar
   * el nombre completo y linkear a su ficha, en vez del `senderName` guardado en cada mensaje.
   */
  const senderIds = useMemo(
    () =>
      rows
        .filter((r) => r.kind === "message" && r.payload.senderType === "operator")
        .map((r) => r.payload.senderId ?? 0)
        .filter((id) => id > 0),
    [rows],
  );
  const { push } = useNavigation();
  const operatorRefs = useOperatorRefs(senderIds, conversation?.store_code);

  const mentionOptions = useMemo(
    () =>
      // `value` es lo que antd inserta en el textarea. Tiene que ser el NOMBRE: con el marcador
      // crudo el operador veía `@[gaston amuchastegui](30231)` mientras escribía. El marcador se
      // reconstruye al enviar (`toStoredText`), igual que en el Historial — ver mention-markers.ts.
      mentionables.map((m) => ({
        value: m.name,
        label: agentIds.has(m.userId) ? (
          <Space size={6}>
            <AgentIcon style={{ color: "#00733B" }} />
            {m.name}
          </Space>
        ) : (
          m.name
        ),
        key: String(m.userId),
      })),
    [mentionables, agentIds],
  );

  /** Nombres que el espejo del input pinta como chip. */
  const mentionNames = useMemo(() => mentionables.map((m) => m.name), [mentionables]);

  /**
   * Resolver el contacto para el nombre y los contadores de la cabecera.
   *
   * findByPhone busca por teléfono; si no matchea pero la conversación ya trae contact_id
   * (lo da el backend en la lista), lo usamos directamente para no perder quotes/orders.
   *
   * Está extraído en un callback porque se llama desde **dos** lados: al abrir el chat y al
   * llegar `conversation:update` con el contacto recién vinculado. En ese segundo caso el
   * contacto se creó *durante* el request del timeline, o sea después de que esto ya devolvió
   * null — sin volver a pedirlo, la cabecera se queda con el teléfono hasta recargar la página.
   */
  const resolveHeaderContact = useCallback(async () => {
    if (!conversation) return;
    const cid = conversation.conversation_id;
    // Reemplaza al flag `active` del efecto: como ahora hay dos llamadores, la cancelación no
    // puede vivir en el cleanup de uno solo. Si mientras esperábamos cambiaron de conversación,
    // el resultado ya no aplica.
    const sigueVigente = () => headerConvRef.current === cid;
    try {
      // Camino rápido (Q6): la conversación ya trae el `contact_id`, así que se pide la ficha por
      // clave primaria y NO se busca por teléfono.
      //
      // La búsqueda por teléfono va contra `/contacts/search`, que matchea con `LIKE '%dígitos%'`:
      // sin índice posible, 4 joins —uno con un `OR` en el `ON` que fuerza barrido de `permissions`—
      // y `GROUP_CONCAT` con tabla temporal y filesort. Es la de los ~5 s de #136. Buscar por
      // teléfono un contacto cuya clave primaria ya tenemos en la mano es al revés.
      //
      // Y además de lenta es **ambigua**: el substring puede matchear en el medio de otro número, y
      // como el endpoint corta en 5 filas ordenadas por `updated_at`, el contacto correcto puede
      // quedar afuera del recorte y no encontrarse nunca.
      const conocido = conversation.contact_id ?? undefined;
      if (conocido) {
        const detail = await contactsApi.getContactDetail(conocido).catch(() => null);
        if (!sigueVigente()) return;
        setHeaderContactDetail(detail);
        const nombre =
          [detail?.firstname, detail?.lastname].filter(Boolean).join(" ").trim() ||
          conversation.contact_name ||
          "";
        setHeaderContact({
          contactId: conocido,
          name: nombre,
          userId: detail?.userId ?? undefined,
          company: detail?.company,
          email: undefined,
          whatsapp: undefined,
        });
        return;
      }

      // Sin contacto vinculado no queda otra que buscar por teléfono: es un hilo que arrancó el
      // cliente y todavía no se resolvió a nadie.
      const c = await contactsApi.findByPhone(
        conversation.external_thread_id,
        conversation.store_code,
      );
      if (!sigueVigente()) return;
      if (!c?.contactId) {
        setHeaderContact(c);
        return;
      }
      const detail = await contactsApi.getContactDetail(c.contactId).catch(() => null);
      if (!sigueVigente()) return;
      setHeaderContactDetail(detail);
      // userId lo necesita ordersByContact; si findByPhone no lo trajo, lo saca del detail.
      setHeaderContact({ ...c, userId: c.userId ?? detail?.userId ?? undefined });
    } catch {
      /* sin match → se queda el número */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    conversation?.conversation_id,
    conversation?.external_thread_id,
    conversation?.store_code,
    conversation?.contact_id,
    conversation?.contact_name,
  ]);

  useEffect(() => {
    setHeaderContact(null);
    setHeaderContactDetail(null);
    headerConvRef.current = convId;
    linkedContactRef.current = null;
    void resolveHeaderContact();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convId]);

  // Contadores de quotes y orders para el header del chat.
  // Usa los mismos endpoints del panel para no tener filtros de fecha distintos.
  // También se recargan cuando una nota nombra un presupuesto nuevo —el que acaba de crear el
  // asistente— (#639); en ese caso no se vuelven a 0 mientras cargan, para que no parpadeen.
  const contadoresDe = useRef<string | null>(null);
  useEffect(() => {
    const deQuien = `${headerContact?.contactId ?? ""}:${headerContact?.userId ?? ""}`;
    if (contadoresDe.current !== deQuien) {
      contadoresDe.current = deQuien;
      setQuotesCount(0);
      setOrdersCount(0);
    }
    if (!conversation?.store_code) return;
    const { contactId, userId } = headerContact ?? {};
    if (!contactId && !userId) return;
    let active = true;
    if (contactId) {
      contextApi
        .quotesByContact(contactId, conversation.store_code)
        .then((qs) => { if (active) setQuotesCount(qs.length); })
        .catch(() => {});
    }
    if (userId) {
      contextApi
        .ordersByContact(userId, conversation.store_code)
        .then((os) => { if (active) setOrdersCount(os.length); })
        .catch(() => {});
    }
    return () => { active = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerContact?.contactId, headerContact?.userId, presupuestosNombrados]);

  // Cargar timeline + programados + join room + markRead al cambiar de conversación.
  useEffect(() => {
    if (!convId) return;
    // Si ya lo miramos en esta sesión, se pinta al instante y sin skeleton. Igual se revalida
    // abajo: el cache sirve para pintar rápido, no para ahorrarse el request (ver timeline-cache).
    const cacheado = leerTimeline(convId);
    if (cacheado?.length) {
      setRows(cacheado);
      setLoading(false);
    } else {
      setRows([]);
      setLoading(true);
    }
    setScheduledList([]);
    setProgramOpen(false);
    // 015 F5: al cambiar de conversación se reinicia el estado del "cargar más". Que
    // `historyExhausted` vuelva a false es a propósito: si el operador reabre el chat, se le permite
    // volver a preguntarle a WhatsApp una vez (la alternativa era persistirlo en DB).
    setHasMoreDb(true);
    setHistoryExhausted(false);
    setLoadingMore(false);
    setWaitingHistory(false);
    setHistoryError(false);
    if (historyTimeoutRef.current) clearTimeout(historyTimeoutRef.current);
    prependHeightRef.current = null;
    socket?.emit("conversation:join", { conversationId: convId });
    // Guarda de respuesta tardía. Saltando rápido A→B→A, el `getTimeline` de B puede resolver
    // DESPUÉS del de A: sin esto, las filas de B se escribían con `convId` ya en A y —peor— el cache
    // las persistía bajo la clave de A. Antes se auto-curaba, porque cada apertura reemplazaba las
    // filas con un fetch fresco; ahora quedan guardadas.
    let vigente = true;
    conversationsApi
      .getTimeline(convId)
      .then((r) => {
        if (!vigente) return;
        // La ventana fresca se junta con lo cacheado: si el operador había traído mensajes viejos
        // con "cargar más", no se pierden ni salta el scroll.
        setRows(reconciliarTimeline(cacheado, r.slice().reverse()));
        // La primera página vino incompleta ⇒ no hay más en la DB, el próximo "cargar más" va
        // directo a WhatsApp. (El default del backend es 50.)
        if (r.length < 50) setHasMoreDb(false);
      })
      .catch(() => {})
      .finally(() => {
        if (vigente) setLoading(false);
      });
    conversationsApi.listScheduled(convId).then(setScheduledList).catch(() => {});
    // Optimista: el badge se apaga en el acto, sin esperar el round-trip. Y si el POST falla se cae
    // al refetch completo —lo que pasaba SIEMPRE antes— para que nadie quede con el contador en
    // cero mintiendo. El camino caro sobrevive sólo como plan B.
    onRead(convId);
    conversationsApi.markRead(convId).catch(() => onStatusChange());
    socket?.emit("conversation:read", { conversationId: convId });
    return () => {
      vigente = false;
      socket?.emit("conversation:leave", { conversationId: convId });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convId]);

  /**
   * Mantiene el cache al día. Va sobre `rows` y no sobre cada `setRows` porque hay diez lugares que
   * los mutan —socket, tildes, notas, "cargar más"— y uno solo acá los cubre a todos.
   *
   * La guarda del ref NO es decorativa: en el commit donde cambia `convId`, `rows` todavía tiene
   * las filas del chat ANTERIOR (el `setRows` del efecto de arriba recién aterriza en el commit
   * siguiente). Sin saltear esa primera pasada, se guardaría el timeline del chat viejo bajo la
   * clave del nuevo.
   */
  const convDeLasFilas = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!convId) return;
    if (convDeLasFilas.current !== convId) {
      convDeLasFilas.current = convId;
      return;
    }
    guardarTimeline(convId, rows);
  }, [convId, rows]);

  // Mensajes nuevos en tiempo real para la conversación abierta.
  useEffect(() => {
    if (!socket || !convId) return;
    const handler = (p: { conversationId: string; message: any }) => {
      if (p.conversationId !== convId) return;
      const m = p.message ?? {};
      setRows((prev) => {
        if (m.messageId && prev.some((r) => r.payload.messageId === m.messageId)) return prev;
        return [
          ...prev,
          {
            kind: "message",
            // Sin `messageId` el id tiene que ser igual DETERMINÍSTICO: uno aleatorio no matchea nunca
        // la ventana fresca, así que al reconciliar se trataba como "más viejo que todo" y el
        // mensaje saltaba al tope del chat en la próxima apertura.
        id: m.messageId ?? `sin-id:${m.sentAt ?? ""}:${m.senderType ?? ""}`,
            occurredAt: m.sentAt ?? new Date().toISOString(),
            payload: {
              messageId: m.messageId,
              // Segunda llave para encontrar esta fila después. `message:status` ya lo usaba; la
              // reparación no podía, porque no se guardaba.
              providerMessageId: m.providerMessageId,
              senderType: m.senderType,
              senderName: m.senderName,
              senderId: m.senderId,
              contentType: m.contentType,
              content: m.content,
              mediaUrl: m.mediaUrl,
              status: m.status,
              template: m.template ?? null,
              pricing: m.pricing ?? null,
            },
          },
        ];
      });
    };
    // Acuses (tildes reales): avanza el status del saliente.
    const statusHandler = (p: {
      conversationId: string;
      messageId?: string;
      providerMessageId?: string;
      status: string;
      errorCode?: string | null;
    }) => {
      if (p.conversationId !== convId) return;
      // Cloud API: un rechazo de Meta después de aceptado trae su código (la ventana, el pago).
      if (p.status === "failed") aplicarEfecto(efectoDelAcuseFallido(p.errorCode));
      setRows((prev) =>
        prev.map((r) =>
          r.kind === "message" &&
          ((p.messageId && r.payload.messageId === p.messageId) ||
            (p.providerMessageId && r.payload.providerMessageId === p.providerMessageId))
            ? { ...r, payload: { ...r.payload, status: p.status } }
            : r,
        ),
      );
    };
    // Notas en tiempo real: general → fila propia; anidada → dentro de la burbuja.
    const noteNewHandler = (p: { conversationId: string; note: NoteView }) => {
      if (p.conversationId !== convId || !p.note) return;
      applyNote(p.note);
      // D65: la nota que confirma una tarea se escribe después de crearla; la franja la relee para
      // saber a qué nota lleva.
      if (p.note.task) releerTareas(convId);
    };
    // D49: alguien eligió una opción de una nota del asistente; la nota se actualiza en su lugar.
    const noteUpdatedHandler = (p: { conversationId: string; note: NoteView }) => {
      if (p.conversationId !== convId || !p.note) return;
      setRows((prev) => actualizarNota(prev, p.note));
    };
    const noteDeletedHandler = (p: { conversationId: string; noteId: string; messageId?: string | null }) => {
      if (p.conversationId !== convId) return;
      removeNote(p.noteId, p.messageId);
    };
    // Programados: upsert por scheduledId (crear/cancelar/enviar/fallar llegan por acá).
    const scheduledHandler = (p: { conversationId: string; scheduled: ScheduledMessage }) => {
      if (p.conversationId !== convId || !p.scheduled) return;
      setScheduledList((prev) => {
        const rest = prev.filter((s) => s.scheduledId !== p.scheduled.scheduledId);
        return [...rest, p.scheduled];
      });
    };
    // D56: lo que el asistente está haciendo. Llega la lista entera: el último gana.
    const actividadHandler = (p: { conversationId: string; items: ActividadDelAsistente[] }) => {
      if (p.conversationId !== convId) return;
      setActividad(p.items ?? []);
    };
    // D65: una tarea de la conversación cambió —se creó, se canceló, cambió de hora, empezó o
    // terminó—. El aviso no trae la tarea: se relee la lista.
    const tareasHandler = (p: { conversationId: string }) => {
      if (p?.conversationId !== convId) return;
      releerTareas(convId);
    };
    // D57: un audio que empezó a transcribirse, terminó o falló.
    const transcriptHandler = (p: {
      conversationId: string;
      messageId: string;
      transcript: string | null;
      transcriptStatus: EstadoDeTranscripcion;
    }) => {
      if (p.conversationId !== convId) return;
      setRows((prev) => aplicarTranscripcion(prev, p));
    };
    // Reconexión del socket: el room conv-{id} se perdió en el server → re-join + resync del
    // timeline y los programados (por si pasó algo mientras estuvimos desconectados).
    const reconnectHandler = () => {
      socket.emit("conversation:join", { conversationId: convId });
      conversationsApi.getAgentActivity(convId).then(recibirActividad).catch(() => {});
      releerTareas(convId);
      conversationsApi
        .getTimeline(convId)
        .then((r) => setRows(r.slice().reverse()))
        .catch(() => {});
      conversationsApi.listScheduled(convId).then(setScheduledList).catch(() => {});
    };
    // 015 F5: historial que pedimos a WhatsApp. Llega por un evento PROPIO justamente porque va
    // arriba: si viniera por `message:new` se appendearía al pie del chat, debajo de los recientes.
    const historyHandler = (p: { conversationId: string; message: any }) => {
      if (p.conversationId !== convId) return;
      const m = p.message ?? {};
      if (!m.messageId && !m.providerMessageId) return;
      // Llegó historial: se cancela la espera y el cartel de error.
      if (historyTimeoutRef.current) clearTimeout(historyTimeoutRef.current);
      setWaitingHistory(false);
      setHistoryError(false);
      prependRows([
        {
          kind: "message",
          id: m.messageId ?? m.providerMessageId,
          occurredAt: m.sentAt ?? new Date().toISOString(),
          payload: {
            messageId: m.messageId,
            senderType: m.senderType,
            senderName: m.senderName,
            senderId: m.senderId,
            contentType: m.contentType,
            content: m.content,
            mediaUrl: m.mediaUrl,
            status: m.status,
            template: m.template ?? null,
            pricing: m.pricing ?? null,
          },
        } as TimelineRow,
      ]);
    };
    // Un mensaje que había llegado ilegible y WhatsApp reenvió descifrable: se reemplaza el
    // contenido de la burbuja que ya está en pantalla, sin duplicarla ni moverla de lugar.
    const repairedHandler = (p: {
      conversationId: string;
      message: {
        messageId: string;
        providerMessageId?: string | null;
        contentType?: string;
        content?: string | null;
        mediaUrl?: string | null;
      };
    }) => {
      if (p.conversationId !== convId) return;
      // **Se busca por las dos llaves.** Un mensaje que entró en vivo por `message:new` no siempre
      // trae el id interno —hasta ahora NUNCA lo traía—, así que buscarlo sólo por `messageId` no
      // encontraba nada y el `[mensaje en camino…]` se quedaba en pantalla. La reparación llegaba
      // bien; el `map` no matcheaba ninguna fila. Se arreglaba sola con cualquier refetch (recargar,
      // o salir del chat y volver) porque ahí las filas vienen de la base y sí tienen el id.
      const esLaFila = (payload: { messageId?: string; providerMessageId?: string }) =>
        (!!p.message.messageId && payload.messageId === p.message.messageId) ||
        (!!p.message.providerMessageId && payload.providerMessageId === p.message.providerMessageId);
      if (!p.message?.messageId && !p.message?.providerMessageId) return;
      setRows((prev) =>
        prev.map((r) =>
          r.kind === "message" && esLaFila(r.payload)
            ? {
                ...r,
                payload: {
                  ...r.payload,
                  // Adopta el id interno si no lo tenía: a partir de acá se la encuentra por
                  // cualquiera de las dos llaves, igual que a una fila traída de la base.
                  messageId: p.message.messageId ?? r.payload.messageId,
                  contentType: p.message.contentType ?? r.payload.contentType,
                  content: p.message.content ?? null,
                  mediaUrl: p.message.mediaUrl ?? null,
                },
              }
            : r,
        ),
      );
    };
    // Reacción puesta o quitada (030 F2). Parchea la burbuja: una reacción NO es una fila del
    // timeline. Y busca por las dos llaves, por lo mismo que la reparación — un mensaje que entró
    // en vivo puede no tener el id interno todavía.
    const reaccionHandler = (p: ReactionUpdate) => {
      if (p?.conversationId !== convId) return;
      setRows((prev) => aplicarReaccion(prev, p));
    };
    // Cierre del pedido: `count = 0` ⇒ WhatsApp no tiene más historial para este chat.
    const historyDoneHandler = (p: { conversationId: string; count: number; exhausted: boolean }) => {
      if (p.conversationId !== convId) return;
      if (historyTimeoutRef.current) clearTimeout(historyTimeoutRef.current);
      setWaitingHistory(false);
      setHistoryError(false);
      if (p.exhausted || p.count === 0) setHistoryExhausted(true);
      setLoadingMore(false);
    };

    /**
     * El backend vinculó (o creó) el contacto de esta conversación. Llega por dos rooms —la de la
     * conversación y la del usuario— así que puede venir duplicado: se ignora si ya tenemos ese
     * contactId, para no pedir la ficha dos veces.
     */
    const contactLinkedHandler = (p: {
      conversationId: string;
      contactId?: number;
      contactName?: string | null;
      serviceWindowExpiresAt?: string | null;
    }) => {
      if (p.conversationId !== convId) return;
      // D27: el contacto escribió y la ventana se renovó; el compose se habilita sin recargar.
      if (p.serviceWindowExpiresAt) {
        setWindowExpiresAt((prev) => ventanaMasReciente(prev, p.serviceWindowExpiresAt));
        setNow(new Date());
      }
      if (!p.contactId) return;
      if (linkedContactRef.current === p.contactId) return;
      linkedContactRef.current = p.contactId;
      void resolveHeaderContact();
    };

    // D10: un borrado para todos, del contacto o desde el celular del store.
    const deletedHandler = (p: {
      conversationId: string;
      messageId?: string;
      providerMessageId?: string | null;
      deletedAt?: string | null;
    }) => {
      if (p.conversationId !== convId) return;
      setRows((prev) => aplicarBorrado(prev, p));
    };

    socket.on("conversation:update", contactLinkedHandler);
    socket.on("message:deleted", deletedHandler);
    socket.on("message:new", handler);
    socket.on("message:history", historyHandler);
    socket.on("message:repaired", repairedHandler);
    socket.on("message:reaction", reaccionHandler);
    socket.on("history:done", historyDoneHandler);
    socket.on("message:status", statusHandler);
    socket.on("note:new", noteNewHandler);
    socket.on("note:deleted", noteDeletedHandler);
    socket.on("note:updated", noteUpdatedHandler);
    socket.on("scheduled:update", scheduledHandler);
    socket.on("agent:activity", actividadHandler);
    socket.on("agent:tasks", tareasHandler);
    socket.on("message:transcript", transcriptHandler);
    socket.on("connect", reconnectHandler);
    return () => {
      socket.off("agent:activity", actividadHandler);
      socket.off("agent:tasks", tareasHandler);
      socket.off("message:transcript", transcriptHandler);
      socket.off("conversation:update", contactLinkedHandler);
      socket.off("message:deleted", deletedHandler);
      socket.off("message:new", handler);
      socket.off("message:history", historyHandler);
      socket.off("message:repaired", repairedHandler);
      socket.off("message:reaction", reaccionHandler);
      socket.off("history:done", historyDoneHandler);
      socket.off("message:status", statusHandler);
      socket.off("note:new", noteNewHandler);
      socket.off("note:deleted", noteDeletedHandler);
      socket.off("note:updated", noteUpdatedHandler);
      socket.off("scheduled:update", scheduledHandler);
      socket.off("connect", reconnectHandler);
    };
  }, [socket, convId, recibirActividad, releerTareas]);

  // D56: al abrir la conversación, lo que el asistente esté haciendo ya; después lo trae el socket.
  useEffect(() => {
    convIdActual.current = convId;
    setActividad([]);
    if (!convId) return;
    conversationsApi.getAgentActivity(convId).then(recibirActividad).catch(() => {});
  }, [convId, recibirActividad]);

  // D65: al abrir la conversación, sus tareas; lo de la anterior no se ve ni un instante.
  useEffect(() => {
    despacharFranja({ tipo: "abrir", conversationId: convId ?? null });
    if (convId) releerTareas(convId);
  }, [convId, releerTareas]);

  // D56: mientras hay algo en pantalla, se vuelve a preguntar cada minuto. Un pod caído no avisa
  // nada, y el servidor deja de mostrar un pedido que nadie retira: sin esto, el «pensando…» quedaría.
  useEffect(() => {
    if (!convId || actividad.length === 0) return;
    const t = setInterval(() => {
      conversationsApi.getAgentActivity(convId).then(recibirActividad).catch(() => {});
    }, 60_000);
    return () => clearInterval(t);
  }, [convId, actividad.length, recibirActividad]);

  /** D57: pedir la transcripción de un audio. El texto llega por `message:transcript`. */
  async function doTranscribir(messageId: string) {
    if (!convId) return;
    setRows((prev) => aplicarTranscripcion(prev, { messageId, transcript: null, transcriptStatus: "pending" }));
    try {
      const r = await conversationsApi.requestTranscription(convId, messageId);
      setRows((prev) => aplicarTranscripcion(prev, r));
    } catch (e: any) {
      setRows((prev) => aplicarTranscripcion(prev, { messageId, transcript: null, transcriptStatus: "failed" }));
      antdMessage.error(`No se pudo transcribir: ${e?.response?.data?.message ?? e?.message ?? e}`);
    }
  }

  /**
   * Inserta una nota en el estado local (dedup por noteId; WS + POST optimista), en el mensaje que
   * dice su `messageId`. Si ese mensaje no está en pantalla, suelta hasta que se cargue (D67).
   */
  function applyNote(note: NoteView) {
    setRows((prev) => ponerNota(prev, note));
  }

  function removeNote(noteId: string, messageId?: string | null) {
    setRows((prev) =>
      prev
        .filter((r) => !(r.kind === "note" && r.id === noteId))
        .map((r) =>
          messageId && r.kind === "message" && r.payload.messageId === messageId
            ? { ...r, payload: { ...r.payload, notes: (r.payload.notes ?? []).filter((n) => n.noteId !== noteId) } }
            : r,
        ),
    );
  }

  // Auto-scroll al fondo cuando llegan/cargan mensajes — salvo cuando lo que pasó fue un PREPEND
  // (015 F5): ahí hay que quedarse donde estaba el operador, corriendo el scroll por lo que creció
  // el contenido. Sin esto, cargar más viejo te patea al fondo del chat.
  useEffect(() => {
    const body = bodyRef.current;
    if (!body) return;
    const prevHeight = prependHeightRef.current;
    if (prevHeight !== null) {
      prependHeightRef.current = null;
      body.scrollTop += body.scrollHeight - prevHeight;
      return;
    }
    body.scrollTo({ top: body.scrollHeight });
  }, [rows]);

  /**
   * Prependea filas viejas sin duplicar y manteniendo el orden cronológico. Guarda el alto previo
   * para que el efecto de scroll conserve la posición de lectura.
   */
  function prependRows(incoming: TimelineRow[]) {
    if (!incoming.length) return;
    prependHeightRef.current = bodyRef.current?.scrollHeight ?? null;
    setRows((prev) => {
      const seen = new Set(prev.map((r) => r.id));
      const fresh = incoming.filter((r) => !seen.has(r.id));
      if (!fresh.length) {
        prependHeightRef.current = null;
        return prev;
      }
      return [...fresh, ...prev].sort((a, b) => a.occurredAt.localeCompare(b.occurredAt));
    });
  }

  /**
   * "Cargar más" (015 F5). Cascada: mientras haya en la DB se pagina de ahí (instantáneo); cuando se
   * agota, se le pide a WhatsApp de a 10 — eso no devuelve mensajes, llegan por `message:history`.
   */
  async function loadMore() {
    if (!convId || loading || loadingMore) return;
    if (!hasMoreDb && (historyExhausted || sinHistorial)) return;
    setLoadingMore(true);
    try {
      if (hasMoreDb) {
        const oldest = rows[0]?.occurredAt;
        const page = await conversationsApi.getTimeline(convId, oldest, HISTORY_PAGE);
        prependRows(page.slice().reverse());
        if (page.length < HISTORY_PAGE) setHasMoreDb(false);
        return;
      }
      const { exhausted } = await conversationsApi.fetchHistory(convId, HISTORY_PAGE);
      // `exhausted` acá = no se pudo ni pedir (sin sesión, sin mensaje de referencia). Si el pedido
      // salió, la respuesta la cierra `history:done`.
      if (exhausted) {
        setHistoryExhausted(true);
        return;
      }
      // El pedido viaja al teléfono y puede quedar sin respuesta (WhatsApp no garantiza contestar
      // los pedidos de historial). Sin este timeout el operador se queda mirando un spinner para
      // siempre: se avisa y se lo deja reintentar.
      setWaitingHistory(true);
      if (historyTimeoutRef.current) clearTimeout(historyTimeoutRef.current);
      historyTimeoutRef.current = setTimeout(() => {
        setWaitingHistory(false);
        setHistoryError(true);
      }, HISTORY_WAIT_MS);
    } catch {
      // Un error de red no debe dejar el botón trabado: se puede reintentar.
    } finally {
      setLoadingMore(false);
    }
  }

  // Dispara el "cargar más" cuando el tope del scroll entra en viewport.
  useEffect(() => {
    const sentinel = topRef.current;
    if (!sentinel || loading) return;
    const obs = new IntersectionObserver(
      (entries) => {
        // Autocarga solo lo local: pedirle al teléfono es siempre por acción explícita del botón.
        if (entries[0]?.isIntersecting && hasMoreDb) void loadMore();
      },
      { root: bodyRef.current, threshold: 0.1 },
    );
    obs.observe(sentinel);
    return () => obs.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convId, loading, loadingMore, hasMoreDb, historyExhausted, rows.length]);

  // Pinta el mensaje devuelto por el POST (optimista). El eco WS luego deduplica por messageId.
  function appendLocal(m: any) {
    if (!m?.messageId) return;
    setRows((prev) => {
      if (prev.some((r) => r.payload.messageId === m.messageId)) {
        // El eco por socket pudo llegar antes que la respuesta del POST, sin el template: se completa.
        if (!m.template) return prev;
        return prev.map((r) =>
          r.payload.messageId === m.messageId && !r.payload.template ? { ...r, payload: { ...r.payload, template: m.template } } : r,
        );
      }
      return [
        ...prev,
        {
          kind: "message",
          id: m.messageId,
          occurredAt: m.sentAt ?? new Date().toISOString(),
          payload: {
            messageId: m.messageId,
            senderType: m.senderType ?? "operator",
            senderName: m.senderName,
            senderId: m.senderId,
            contentType: m.contentType,
            content: m.content,
            mediaUrl: m.mediaUrl,
            status: m.status ?? "sent",
            template: m.template ?? null,
            pricing: m.pricing ?? null,
          },
        },
      ];
    });
  }

  /**
   * Reintenta un saliente fallido. Va sobre la misma fila: el backend reusa el contenido guardado,
   * así que no se duplica el mensaje en el hilo.
   *
   * Es manual y no automático a propósito: si WhatsApp llegó a recibirlo pero se perdió la
   * confirmación, el reintento se lo manda al cliente dos veces. Lo decide el operador, que ve si
   * el cliente respondió o no.
   */
  async function doRetry(messageId: string) {
    if (!convId) return;
    setRetrying(messageId);
    try {
      await conversationsApi.retryMessage(convId, messageId);
      // El estado final llega por `message:status`; no se toca el estado local acá para no pintar
      // un tilde que el backend todavía no confirmó.
    } catch (e: any) {
      avisarErrorDeEnvio(e, "No se pudo reenviar");
    } finally {
      setRetrying(null);
    }
  }

  async function doSend(thenClose = false) {
    const t = text.trim();
    if ((!t && !attachment) || !convId) return;
    setSending(true);
    try {
      let sent: any;
      if (attachment) {
        const up = await conversationsApi.uploadMedia(convId, attachment);
        sent = await conversationsApi.sendMediaMessage(convId, {
          contentType: up.contentType,
          mediaUrl: up.mediaUrl,
          mediaMimeType: up.mediaMimeType,
          content: t || undefined,
        });
        setAttachment(null);
      } else {
        sent = await conversationsApi.sendMessage(convId, t);
      }
      appendLocal(sent);
      setText("");
      if (thenClose) {
        await conversationsApi.patchStatus(convId, "closed");
        onStatusChange();
      } else if (closed) {
        await conversationsApi.patchStatus(convId, "open");
        onStatusChange();
      }
    } catch (e: any) {
      // #177: si la restricción llegó entre que se dibujó el chat y el envío (o se perdió el evento de
      // socket), refrescar deja el compose bloqueado con el cartel, en vez de dejar al operador
      // reintentando contra un número castigado — cada rechazo suma señal negativa. La ventana
      // cerrada y el envío pausado de la Cloud API bloquean igual.
      avisarErrorDeEnvio(e, "No se pudo enviar");
    } finally {
      setSending(false);
    }
  }

  /**
   * Reacciona a un mensaje, o quita la reacción propia tocando la que ya estaba.
   *
   * **Optimista con reversión.** La reacción aparece en el acto y, si el POST falla, se vuelve al
   * set anterior — no al vacío: el mensaje puede tener reacciones del contacto que no cambiaron.
   *
   * El servidor manda el set completo por socket al confirmar, así que ese evento pisa lo optimista
   * y no hay que reconciliar nada a mano.
   */
  async function doReaccionar(messageId: string, tocado: string) {
    if (!convId) return;
    const fila = rows.find((r) => r.kind === "message" && r.payload.messageId === messageId);
    const antes = fila?.payload.reactions ?? [];
    const emoji = emojiAEnviar(miReaccion(antes, me), tocado);
    const optimista = [
      ...antes.filter((r) => !(r.reactorType === "operator" && r.reactorId === me)),
      ...(emoji
        ? [{ reactorType: "operator" as const, reactorId: me ?? 0, reactorName: "", emoji }]
        : []),
    ];
    setRows((prev) => aplicarReaccion(prev, { conversationId: convId, messageId, reactions: optimista }));
    try {
      await conversationsApi.reactToMessage(convId, messageId, emoji);
    } catch (e: any) {
      setRows((prev) => aplicarReaccion(prev, { conversationId: convId, messageId, reactions: antes }));
      aplicarEfecto(efectoDelError(e));
      antdMessage.error(e?.response?.data?.message ?? e?.message ?? "No se pudo reaccionar");
    }
  }

  /**
   * Crea nota interna (general desde el compose, anidada desde «Agregar nota» en la burbuja).
   *
   * El input trabaja con texto legible (`@gaston amuchastegui`) y acá se reconstruye el marcador
   * persistido (`@[gaston amuchastegui](30231)`), que es lo que el backend parsea para notificar y
   * lo que el timeline usa para pintar el chip. Va en esta función y no en cada call site porque
   * los tres —compose, botón de enviar y la burbuja— pasan por acá.
   */
  async function doCreateNote(content: string, messageId?: string) {
    const t = toStoredText(content.trim(), mentionables);
    if (!t || !convId) return;
    setSending(true);
    try {
      // La cita es del compose: una nota anidada desde «Agregar nota» nunca es una respuesta.
      const note = await conversationsApi.createNote(convId, t, messageId, messageId ? undefined : replyTo?.noteId);
      applyNote(note);
      if (messageId) {
        setNoteTarget(null);
        setNoteText("");
      } else {
        setText("");
        setNoteMode(false);
        setReplyTo(null);
      }
    } catch (e: any) {
      antdMessage.error(`No se pudo crear la nota: ${e?.response?.data?.message ?? e?.message ?? e}`);
    } finally {
      setSending(false);
    }
  }

  /**
   * «Agregar nota» en una nota que está en un mensaje (#633). La respuesta es la de D61 —con la cita,
   * y arroba a quien escribió la original—, y el servidor la deja en ese mensaje si desde la original
   * no se le escribió al cliente (D67). No toca lo que el operador tenga escrito en el compose.
   */
  async function doResponderEnMensaje(content: string, cita: NoteReplyTo) {
    const t = toStoredText(content.trim(), mentionables);
    if (!t || !convId) return;
    setSending(true);
    try {
      applyNote(await conversationsApi.createNote(convId, t, undefined, cita.noteId));
      setNoteTarget(null);
      setNoteText("");
      setRespuestaA(null);
    } catch (e: any) {
      antdMessage.error(`No se pudo crear la nota: ${e?.response?.data?.message ?? e?.message ?? e}`);
    } finally {
      setSending(false);
    }
  }

  /**
   * D49: elige una opción de la nota del asistente. La nota vuelve con la elección y sale la del
   * operador arrobando al asistente. Si otro eligió antes (409), se muestra la que eligió.
   */
  async function doChooseOption(note: NoteView, eleccion: EleccionDeOpcion) {
    if (!convId) return;
    try {
      const r = await conversationsApi.chooseNoteOption(convId, note.noteId, eleccion);
      setRows((prev) => actualizarNota(prev, r.note));
      applyNote(r.reply);
    } catch (e: any) {
      const actual: NoteView | undefined = e?.response?.data?.note;
      if (e?.response?.status === 409 && actual) {
        setRows((prev) => actualizarNota(prev, actual));
        antdMessage.info(`${actual.choices?.answer?.userName ?? "Otro operador"} ya eligió ${actual.choices?.answer?.label ?? ""}`);
        return;
      }
      antdMessage.error(`No se pudo elegir: ${e?.response?.data?.message ?? e?.message ?? e}`);
    }
  }

  /** El link de presupuesto de una nota, al cliente, con el mismo texto que compartir desde la ficha. */
  /** «Enviar al cliente» de una nota: el texto de siempre, o el que corrigió el operador (#633). */
  async function doSendQuoteLink(link: string, texto: string = quoteLinkMessage(link)) {
    if (!convId) return;
    try {
      appendLocal(await conversationsApi.sendMessage(convId, texto));
    } catch (e: any) {
      avisarErrorDeEnvio(e, "No se pudo enviar");
    }
  }

  /**
   * D67: cancela la consulta al asistente de una nota propia. El «está pensando…» se va ya; el socket
   * trae después la actividad que quedó. Si el asistente ya respondió, se relee lo que está haciendo.
   */
  async function doCancelarConsulta(note: NoteView) {
    if (!convId) return;
    try {
      await conversationsApi.cancelAgentRequest(convId, note.noteId);
      setActividad((prev) => sinConsulta(prev, note.noteId));
      antdMessage.success("Consulta cancelada");
    } catch (e: unknown) {
      const { texto, nivel, releer } = errorAlCancelarConsulta(e);
      antdMessage[nivel](texto);
      if (releer) conversationsApi.getAgentActivity(convId).then(recibirActividad).catch(() => {});
    }
  }

  async function doDeleteNote(note: NoteView) {
    if (!convId) return;
    try {
      await conversationsApi.deleteNote(convId, note.noteId);
      removeNote(note.noteId, note.messageId);
    } catch (e: any) {
      antdMessage.error(`No se pudo eliminar la nota: ${e?.response?.data?.message ?? e?.message ?? e}`);
    }
  }

  // ── Tareas programadas del asistente (D65) ───────────────────────────────────
  /** Un 403 o una tarea que ya corrió se explican; si lo que se ve está viejo, se relee. */
  function avisarErrorDeTarea(e: unknown, accion: "cancelar" | "cambiar") {
    const { texto, nivel, releer } = errorDeTarea(e, accion);
    antdMessage[nivel](texto);
    if (releer && convId) releerTareas(convId);
  }

  async function doCancelarTarea(taskId: string) {
    if (!convId) return;
    try {
      const tarea = await conversationsApi.cancelAgentTask(convId, taskId);
      despacharFranja({ tipo: "cambiada", tarea });
      antdMessage.success("Tarea cancelada");
    } catch (e) {
      avisarErrorDeTarea(e, "cancelar");
    }
  }

  /** `true` si quedó: el selector de hora se cierra sólo entonces. */
  async function doCambiarHoraTarea(taskId: string, runAt: string): Promise<boolean> {
    if (!convId) return false;
    try {
      const tarea = await conversationsApi.rescheduleAgentTask(convId, taskId, runAt);
      despacharFranja({ tipo: "cambiada", tarea });
      antdMessage.success(`Reprogramada para el ${fechaDeTarea(tarea.runAt)}`);
      return true;
    } catch (e) {
      avisarErrorDeTarea(e, "cambiar");
      return false;
    }
  }

  // ── Mensajes programados (OpenSpec 003) ────────────────────────────────────
  /** Presets del Figma. "Later today 13:00" se oculta si ya pasó (margen 5 min). */
  const laterToday = dayjs().hour(13).minute(0).second(0);
  const nextMonday = (() => {
    const d = dayjs().day(); // 0=domingo
    const add = d === 0 ? 1 : 8 - d;
    return dayjs().add(add, "day").hour(9).minute(0).second(0);
  })();
  const showLaterToday = laterToday.isAfter(dayjs().add(5, "minute"));

  async function doSchedule(at: Dayjs) {
    const t = text.trim();
    if (!t || !convId) return;
    setSending(true);
    try {
      const sm = await conversationsApi.createScheduled(convId, t, at.toISOString());
      setScheduledList((prev) => [...prev.filter((s) => s.scheduledId !== sm.scheduledId), sm]);
      setText("");
      setProgramOpen(false);
      setCustomDay(null);
      setCustomTime(null);
    } catch (e: any) {
      avisarErrorDeEnvio(e, "No se pudo programar");
    } finally {
      setSending(false);
    }
  }

  async function doCancelScheduled(sm: ScheduledMessage) {
    if (!convId) return;
    try {
      const updated = await conversationsApi.cancelScheduled(convId, sm.scheduledId);
      setScheduledList((prev) => [...prev.filter((s) => s.scheduledId !== sm.scheduledId), updated]);
    } catch (e: any) {
      antdMessage.error(`No se pudo cancelar: ${e?.response?.data?.message ?? e?.message ?? e}`);
    }
  }

  async function doResendScheduled(sm: ScheduledMessage) {
    if (!convId) return;
    try {
      const updated = await conversationsApi.resendScheduled(convId, sm.scheduledId);
      setScheduledList((prev) => [...prev.filter((s) => s.scheduledId !== sm.scheduledId), updated]);
      antdMessage.success("Reenviando: sale en el próximo minuto");
    } catch (e: any) {
      antdMessage.error(`No se pudo reenviar: ${e?.response?.data?.message ?? e?.message ?? e}`);
    }
  }

  const customAt =
    customDay && customTime
      ? customDay.hour(customTime.hour()).minute(customTime.minute()).second(0)
      : null;

  async function toggleClose() {
    if (!convId) return;
    try {
      await conversationsApi.patchStatus(convId, closed ? "open" : "closed");
      onStatusChange();
    } catch (e: any) {
      antdMessage.error(`No se pudo cambiar el estado: ${e?.message ?? e}`);
    }
  }

  // ── Grabación de audio (nota de voz, estilo WhatsApp) ──────────────────────
  function cleanupRec() {
    if (recTimerRef.current) clearInterval(recTimerRef.current);
    recTimerRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    recorderRef.current = null;
    chunksRef.current = [];
    setRecording(false);
    setRecSecs(0);
  }

  async function startRecording() {
    if (!connected || restricted) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/webm")
          ? "audio/webm"
          : "";
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data.size) chunksRef.current.push(e.data);
      };
      recorderRef.current = mr;
      mr.start();
      setRecording(true);
      setRecSecs(0);
      recTimerRef.current = setInterval(() => setRecSecs((s) => s + 1), 1000);
    } catch {
      antdMessage.error("No se pudo acceder al micrófono");
      cleanupRec();
    }
  }

  function stopRecording(send: boolean) {
    const mr = recorderRef.current;
    if (!mr) return cleanupRec();
    mr.onstop = async () => {
      const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
      const cid = convId;
      cleanupRec();
      if (!send || !blob.size || !cid) return;
      const file = new File([blob], `voice-${blob.size}.webm`, { type: "audio/webm" });
      setSending(true);
      try {
        const up = await conversationsApi.uploadMedia(cid, file);
        const sent = await conversationsApi.sendMediaMessage(cid, {
          contentType: up.contentType,
          mediaUrl: up.mediaUrl,
          mediaMimeType: up.mediaMimeType,
        });
        appendLocal(sent);
      } catch (e: any) {
        antdMessage.error(`No se pudo enviar el audio: ${e?.response?.data?.message ?? e?.message ?? e}`);
      } finally {
        setSending(false);
      }
    };
    mr.stop();
  }

  const fmtSecs = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  if (!conversation) {
    return (
      <Flex align="center" justify="center" style={{ flex: 1, height: "100%" }}>
        <Empty description="Elegí una conversación" />
      </Flex>
    );
  }

  // Nombre del contacto si está en el modelo del store; si no, el identificador del canal
  // (whatsapp → número formateado). NO se usa el pushName de WhatsApp.
  const name =
    headerContact?.name ||
    identifierDisplay(conversation.channel_type, conversation.external_thread_id);

  // Sólo los dueños de cada nivel, igual que la fila de la bandeja y que la ficha del presupuesto.
  // Antes se mostraban todas las participaciones del contacto, incluidas las de tipo vacío —que son
  // de CLIENTE, no de operador— y se pintaban como si fueran operadores del store.
  const headerParticipants = ownerParticipations(headerContactDetail?.participations ?? []);

  /**
   * Una nota, suelta en el timeline o dentro de la burbuja de su mensaje (D67). Dentro, la original de
   * una respuesta está arriba en la misma burbuja: la cita sobra (#633).
   */
  const renderNota = (note: NoteView, nested = false) => (
    <NoteBlock
      note={note}
      nested={nested}
      sinCita={nested}
      mine={me != null && me === note.authorUserId}
      delAsistente={agentIds.has(note.authorUserId)}
      onDelete={doDeleteNote}
      onReply={canEdit ? responderNota : undefined}
      onCancelarConsulta={
        puedeCancelarConsulta(note, me, actividadDeNota.get(note.noteId)) ? doCancelarConsulta : undefined
      }
      onJump={irACita}
      canChoose={puedeElegir(note, me, canEdit)}
      onChoose={doChooseOption}
      actividad={actividadDeNota.get(note.noteId)}
      tareas={tareasDeLaNota(note.task, tareasDeCadaNota.get(note.noteId))}
      onCancelarTarea={doCancelarTarea}
      onCambiarHoraTarea={doCambiarHoraTarea}
      sendLink={
        canEdit && bloqueo === null
          ? { onSend: doSendQuoteLink, sent: (l) => linkYaEnviado(rows, l) }
          : undefined
      }
    />
  );

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Cabecera */}
      <div style={{ borderBottom: "1px solid #f0f0f0", padding: "10px 16px", height: 100, boxSizing: "border-box", display: "flex", flexDirection: "column", gap: 8, overflow: "hidden" }}>
        {/* Fila 1: nombre + contadores | canal WA */}
        <Flex align="flex-start" justify="space-between">
          <Flex align="center" gap={8}>
            <Button type="link" style={{ padding: 0, height: "auto" }} onClick={onOpenContact}>
              <Space size={6}>
                <ContactsOutlined style={{ color: "#00733B" }} />
                <Title level={5} style={{ margin: 0, color: "#00733B" }}>
                  {name}
                </Title>
              </Space>
            </Button>
            {/* Contadores Quotes/Orders (últimos 3 meses): solo si es contacto del store. */}
            {headerContact?.contactId && (
              <>
                <Text type="secondary">|</Text>
                <Button
                  type="link"
                  size="small"
                  style={{ color: "#00733B", padding: "0 4px" }}
                  onClick={() => setContextPanel("quotes")}
                >
                  <Flex align="center" gap={4}>
                    <span>{quotesCount}x</span>
                    <PresupuestoIcon />
                  </Flex>
                </Button>
                <Button
                  type="link"
                  size="small"
                  style={{ color: "#00733B", padding: "0 4px" }}
                  onClick={() => setContextPanel("orders")}
                >
                  <Flex align="center" gap={4}>
                    <span>{ordersCount}x</span>
                    <BoxSeam />
                  </Flex>
                </Button>
              </>
            )}
          </Flex>
          <Flex vertical align="flex-end" gap={2}>
            <Space size={6}>
              {/* Conectado, el ícono dice el tipo de canal (#513); caído, el rojo de siempre. */}
              {!connected ? (
                <WhatsAppOutlined style={{ color: "#FF4D4F" }} />
              ) : cloud ? (
                <WhatsAppMetaIcon />
              ) : (
                <WhatsAppQrIcon />
              )}
              <Text type="secondary" style={{ fontSize: 13 }}>
                {conversation.channel_name}
              </Text>
            </Space>
            {restricted ? (
              // #177: se muestra en lugar de "Sin conexión" porque es la causa, no el síntoma. Con
              // el cartel genérico el operador iba a re-escanear el QR, que no levanta nada.
              <Tooltip
                title={
                  cloud
                    ? `Meta restringió este número ${textoVencimiento(conversation?.channel_restricted_until)}.`
                    : `WhatsApp restringió este número ${textoVencimiento(conversation?.channel_restricted_until)}. Re-vincular con QR no lo soluciona.`
                }
              >
                <Space size={2}>
                  <WarningOutlined style={{ color: "#FF4D4F", fontSize: 12 }} />
                  <Text style={{ color: "#FF4D4F", fontSize: 10 }}>Cuenta restringida</Text>
                </Space>
              </Tooltip>
            ) : !connected ? (
              <Space size={2}>
                <WarningOutlined style={{ color: "#FF4D4F", fontSize: 12 }} />
                <Text style={{ color: "#FF4D4F", fontSize: 10 }}>Sin conexión</Text>
              </Space>
            ) : null}
          </Flex>
        </Flex>
        {/* Fila 2: participaciones (store primero, seller después) + search button abajo derecha */}
        {(() => {
          const sellerParts = headerParticipants.filter((p) => p.type === "seller");
          const hasMoreSellers = sellerParts.length > 1;
          const visibleParticipants = headerParticipants.length > 0
            ? [
                ...headerParticipants.filter((p) => p.type === "store"),
                ...(hasMoreSellers && !expandedHeader ? sellerParts.slice(0, 1) : sellerParts),
              ]
            : [];

          return (
        <Flex align="stretch" justify="space-between">
          <div
            ref={sellersScrollRef}
            className={expandedHeader ? "chat-header-sellers" : undefined}
            style={{ height: 46, overflowY: expandedHeader ? "auto" : "hidden", paddingRight: expandedHeader ? 24 : 0, display: "flex", flexDirection: "column", gap: 2, position: "relative" }}
          >
            {visibleParticipants.length > 0 ? (
              visibleParticipants.map((p, idx) => (
                <div key={p.userId ?? idx} ref={hasMoreSellers && p === sellerParts[0] ? firstSellerRowRef : undefined}>
                <Space size={6}>
                  {p.type === "seller"
                    ? <SellerIcon style={{ fontSize: 12, color: "#8C8C8C" }} />
                    : <ShopOutlined style={{ color: "#8C8C8C", fontSize: 12 }} />
                  }
                  <Text type="secondary" style={{ fontSize: 12 }}>{p.identifier}</Text>
                  {p.name && p.name !== "—" && (
                    <>
                      <UserOutlined style={{ color: "#15744b", fontSize: 12 }} />
                      {p.contactId ? (
                        <Link
                          href={`/contacts/${p.contactId}`}
                          onClick={(e) => {
                            e.preventDefault();
                            push(`/contacts/${p.contactId}`);
                          }}
                          style={{ fontSize: 12, fontWeight: 700 }}
                        >
                          {p.name}
                        </Link>
                      ) : (
                        <Text style={{ fontSize: 12, color: "#15744b", fontWeight: 700 }}>{p.name}</Text>
                      )}
                    </>
                  )}
                  {hasMoreSellers && p === sellerParts[0] && (
                    <div
                      onClick={() => setExpandedHeader((v) => !v)}
                      style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
                    >
                      {expandedHeader ? <CollapseIcon /> : <ExpandIcon />}
                    </div>
                  )}
                </Space>
                </div>
              ))
            ) : (
              <Space size={4}>
                <ShopOutlined style={{ color: "#8C8C8C" }} />
                <Text type="secondary" style={{ fontSize: 13 }}>{conversation.store_code}</Text>
              </Space>
            )}
          </div>
          <Flex align="flex-end">
            <Button
              type="text"
              icon={<SearchOutlined />}
              onClick={() => setSearchOpen((v) => !v)}
            />
          </Flex>
        </Flex>
          );
        })()}
      </div>

      {/* Barra de búsqueda en chat */}
      {searchOpen && (
        <Flex
          align="center"
          gap={6}
          style={{ padding: "6px 12px", borderBottom: "1px solid #f0f0f0", background: "#fff" }}
        >
          <Input
            autoFocus
            size="small"
            placeholder="Search"
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onPressEnter={() => gotoMatch(1)}
            style={{ flex: 1 }}
          />
          <Text type="secondary" style={{ fontSize: 12, minWidth: 34, textAlign: "center" }}>
            {matches.length ? `${matchIndex + 1}/${matches.length}` : "0/0"}
          </Text>
          <Button size="small" type="text" icon={<UpOutlined />} disabled={!matches.length} onClick={() => gotoMatch(-1)} />
          <Button size="small" type="text" icon={<DownOutlined />} disabled={!matches.length} onClick={() => gotoMatch(1)} />
          <Button size="small" type="text" icon={<CloseOutlined />} onClick={closeSearch} />
        </Flex>
      )}

      {/* Cuerpo */}
      <div ref={bodyRef} style={{ flex: 1, overflowY: "auto", padding: 16, background: "#fafafa" }}>
        {loading ? (
          <div style={{ textAlign: "center", paddingTop: 40 }}>
            <Spin />
          </div>
        ) : rows.length === 0 ? (
          <Empty description="Sin mensajes" />
        ) : (
          <>
            {/* 015 F5: centinela del "cargar más". Entra en viewport al scrollear al tope y
                autocarga SOLO lo que está en nuestra DB (instantáneo). Ir a buscar a WhatsApp es
                explícito: se lo pide con el botón, para que el operador sepa que sale del teléfono. */}
            <div ref={topRef} style={{ height: 1 }} />
            <div style={{ textAlign: "center", padding: "8px 0" }}>
              {loadingMore || waitingHistory ? (
                <Space size={6}>
                  <Spin size="small" />
                  {waitingHistory && (
                    <Text style={{ fontSize: 12, color: "#8C8C8C" }}>Buscando en WhatsApp…</Text>
                  )}
                </Space>
              ) : hasMoreDb || (!historyExhausted && !sinHistorial) ? (
                <Space direction="vertical" size={0}>
                  <Button type="link" size="small" onClick={() => void loadMore()}>
                    {hasMoreDb ? "Cargar mensajes anteriores" : "Buscar anteriores en WhatsApp"}
                  </Button>
                  {historyError && (
                    <Text style={{ fontSize: 12, color: "#8C8C8C" }}>
                      El teléfono no respondió el pedido de historial. Podés reintentar.
                    </Text>
                  )}
                </Space>
              ) : (
                <Text style={{ fontSize: 12, color: "#8C8C8C" }}>
                  {sinHistorial
                    ? "No hay mensajes anteriores acá. Los más viejos están en el celular."
                    : "No hay mensajes anteriores"}
                </Text>
              )}
            </div>
          </>
        )}
        {rows.length > 0 &&
          rows.map((r) => {
            if (r.kind === "event") {
              // D23: la conversación vino del sync inicial del celular. Lo de antes es de la App, y se dice
              // que no está en el Historial del Contacto porque es lo primero que alguien va a buscar ahí.
              if (r.payload.eventType === "created" && r.payload.actorType === "system") {
                return (
                  <div key={r.id} style={{ textAlign: "center", margin: "10px 0" }}>
                    <Text style={{ fontSize: 12, color: "#8C8C8C" }}>
                      <MobileOutlined /> Importado del celular el {dayjs(r.occurredAt).format("DD/MM HH:mm")}. Estos
                      mensajes vienen de la app WhatsApp Business y no están en el Historial del Contacto.
                    </Text>
                  </div>
                );
              }
              return (
                <div key={r.id} style={{ textAlign: "center", margin: "10px 0" }}>
                  <Text style={{ fontSize: 12, color: "#8C8C8C" }}>
                    {r.payload.actorName} · {eventLabel(r.payload.eventType, (r.payload as any).metadata)} ·{" "}
                    {dayjs(r.occurredAt).format("DD/MM HH:mm")}
                  </Text>
                </div>
              );
            }
            if (r.kind === "note") {
              // Nota interna suelta: bloque amarillo inline en el timeline. La de un mensaje que todavía
              // no estaba en pantalla, en cuanto el mensaje se carga, se ve dentro de él (D67).
              const note = notaDeFila(r);
              if (hilos.adentro.has(r.id)) return null;
              return (
                <div
                  key={r.id}
                  ref={(el) => {
                    msgRefs.current[r.id] = el;
                  }}
                  // A la derecha, como un mensaje enviado (#633).
                  style={{ margin: "8px 0", maxWidth: "80%", marginLeft: "auto" }}
                >
                  {renderNota(note)}
                </div>
              );
            }
            const mine = r.payload.senderType === "operator";
            // Se prefiere el operador resuelto por id: da el nombre completo y la ficha a la que
            // linkear. Si no se pudo resolver —o el mensaje salió del teléfono, `senderId` 0 y
            // "Celular"— se cae al nombre guardado, sin link: nunca queda peor que antes.
            const ref = r.payload.senderId ? operatorRefs[r.payload.senderId] : undefined;
            const etiqueta = mine ? ref?.name || r.payload.senderName : undefined;
            const plantilla =
              esDeTemplate(r.payload) && !estaBorrado(r.payload) && r.payload.content !== MARCADOR_VER_EN_EL_CELULAR ? (
                <Tag style={{ margin: 0, fontSize: 11 }}>Plantilla</Tag>
              ) : null;
            return (
              <div
                key={r.id}
                ref={(el) => {
                  msgRefs.current[r.id] = el;
                }}
                style={{ display: "flex", justifyContent: mine ? "flex-end" : "flex-start", marginBottom: 8 }}
              >
                <div
                  className="chat-bubble"
                  style={{
                    maxWidth: "70%",
                    padding: "8px 12px",
                    borderRadius: 10,
                    // Una plantilla va en el celeste de Meta (#513): se distingue de lo escrito a mano.
                    background: esDeTemplate(r.payload) ? META_AZUL_FONDO : mine ? "#E6F4ED" : "#fff",
                    border: mine ? "none" : "1px solid #f0f0f0",
                    position: "relative",
                    // El mensaje al que se saltó desde el historial, resaltado unos segundos.
                    boxShadow: focusedId === r.id ? "0 0 0 3px #52c41a66" : undefined,
                    transition: "box-shadow 0.4s ease",
                  }}
                >
                  {etiqueta &&
                    (() => {
                      const estilo = {
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#595959",
                      } as const;
                      // La etiqueta "Plantilla" va en la línea del nombre (#513), no arriba del texto.
                      if (!ref)
                        return (
                          <Flex align="center" gap={6} wrap>
                            <Text style={estilo}>[{etiqueta}]</Text>
                            {plantilla}
                          </Flex>
                        );
                      return (
                        <Flex align="center" gap={6} wrap>
                        <Text style={estilo}>
                          [
                          {/* `Typography.Link` toma `colorLink` del theme (#15744b) junto con sus
                              estados hover y active, en vez de hardcodear el verde acá. */}
                          <Link
                            href={`/contacts/${ref.contactId}`}
                            onClick={(e) => {
                              // Navegación del router: recargar la SPA entera perdería el chat abierto.
                              e.preventDefault();
                              e.stopPropagation();
                              push(`/contacts/${ref.contactId}`);
                            }}
                            style={{ fontSize: 13, fontWeight: 600 }}
                          >
                            {etiqueta}
                          </Link>
                          ]
                        </Text>
                        {plantilla}
                        </Flex>
                      );
                    })()}
                  <div style={{ color: "#262626", fontSize: 14 }}>
                    {estaBorrado(r.payload) ? (
                      // D10: borrado para todos. El contenido no se muestra, como en WhatsApp.
                      <Text type="secondary" italic>
                        <StopOutlined style={{ marginRight: 6 }} />
                        Este mensaje fue eliminado
                      </Text>
                    ) : r.payload.content === MARCADOR_VER_EN_EL_CELULAR ? (
                      // Un tipo que la Cloud API no entrega (encuestas, ver una vez, etc.).
                      <Text type="secondary" italic>
                        <MobileOutlined style={{ marginRight: 6 }} />
                        Este mensaje no se puede mostrar acá: miralo en el celular
                      </Text>
                    ) : (
                    <>
                    {/* Sin nombre arriba, la etiqueta queda donde estaba. */}
                    {!etiqueta && plantilla && <div style={{ marginBottom: 4 }}>{plantilla}</div>}
                    {r.payload.mediaUrl && renderMedia(r.payload.contentType, r.payload.mediaUrl)}
                    {r.payload.contentType === "audio" && r.payload.messageId && (
                      <TranscripcionDelAudio
                        transcript={r.payload.transcript}
                        estado={r.payload.transcriptStatus}
                        puedePedir={canEdit}
                        onPedir={() => void doTranscribir(r.payload.messageId!)}
                      />
                    )}
                    {(r.payload.content ?? "") !== "" && (
                      <div style={{ marginTop: r.payload.mediaUrl ? 4 : 0 }}>
                        {renderMessageText(
                          r.payload.content ?? "",
                          searchText,
                          r.id === currentMatchId,
                        )}
                      </div>
                    )}
                    </>
                    )}
                  </div>
                  {/* Las notas del mensaje, en orden: las que se escribieron acá (⋮ → Agregar nota) y la
                      charla con el asistente que siguió acá, que el servidor guarda en este mensaje
                      (D67). Con ellas, las que llegaron sueltas antes de que el mensaje se cargara. */}
                  {[...(r.payload.notes ?? []), ...(hilos.porMensaje.get(r.payload.messageId ?? "") ?? [])]
                    .sort((a, b) => Date.parse(a.createdAt ?? "") - Date.parse(b.createdAt ?? ""))
                    .map((n) => (
                      <div
                        key={n.noteId}
                        ref={(el) => {
                          msgRefs.current[n.noteId] = el;
                        }}
                      >
                        {renderNota(n, true)}
                      </div>
                    ))}
                  {/* La nota nueva se escribe dentro de la burbuja, como en el Historial (#633). */}
                  {noteTarget !== null && noteTarget === r.payload.messageId && (
                    <NotaEnLinea
                      value={noteText}
                      onChange={setNoteText}
                      placeholder={
                        respuestaA ? `Respuesta a ${respuestaA.authorName} — el contacto no la ve (Esc cancela)` : undefined
                      }
                      onSend={() =>
                        void (respuestaA ? doResponderEnMensaje(noteText, respuestaA) : doCreateNote(noteText, noteTarget))
                      }
                      onCancel={() => {
                        setNoteTarget(null);
                        setNoteText("");
                        setRespuestaA(null);
                      }}
                      sending={sending}
                      options={mentionOptions}
                      names={mentionNames}
                    />
                  )}
                  {/* Reacciones (030 F2): pegadas a la burbuja, NO como fila del timeline. */}
                  {agruparPorEmoji(r.payload.reactions).length > 0 && (
                    <Flex gap={4} style={{ marginTop: 4 }}>
                      {agruparPorEmoji(r.payload.reactions).map(({ emoji, quienes }) => (
                        <Tooltip key={emoji} title={quienes.join(", ")}>
                          <span
                            onClick={
                              canEdit && r.payload.messageId
                                ? () => void doReaccionar(r.payload.messageId!, emoji)
                                : undefined
                            }
                            style={{
                              fontSize: 14,
                              lineHeight: 1,
                              padding: "2px 6px",
                              borderRadius: 10,
                              background: "#fff",
                              // La propia se marca: es la que se saca tocándola, el gesto de WhatsApp.
                              border:
                                miReaccion(r.payload.reactions, me) === emoji
                                  ? `1px solid ${BRAND}`
                                  : "1px solid #f0f0f0",
                              cursor: canEdit ? "pointer" : "default",
                            }}
                          >
                            {emoji}
                            {quienes.length > 1 && (
                              <span style={{ fontSize: 11, color: "#8C8C8C", marginLeft: 3 }}>
                                {quienes.length}
                              </span>
                            )}
                          </span>
                        </Tooltip>
                      ))}
                    </Flex>
                  )}
                  <Flex justify="flex-end" align="center" gap={4}>
                    <Text style={{ fontSize: 11, color: "#8C8C8C" }}>
                      {dayjs(r.occurredAt).format("HH:mm")}
                    </Text>
                    {mine && <StatusTicks status={r.payload.status} />}
                    {mine && r.payload.status === "failed" && r.payload.messageId && (
                      <Tooltip title="Reintentar el envío">
                        <Button
                          type="text"
                          size="small"
                          icon={<RedoOutlined style={{ fontSize: 11 }} />}
                          loading={retrying === r.payload.messageId}
                          onClick={(e) => {
                            e.stopPropagation();
                            void doRetry(r.payload.messageId!);
                          }}
                          style={{ height: 18, padding: "0 4px", color: "#FF4D4F" }}
                        />
                      </Tooltip>
                    )}
                    {canEdit && r.payload.messageId && (
                      <Dropdown
                        trigger={["click"]}
                        menu={{
                          items: [
                            { key: "add-note", label: "Agregar nota" },
                            {
                              key: "reaccionar",
                              label: "Reaccionar",
                              children: EMOJIS_REACCION.map((e) => ({ key: `react:${e}`, label: e })),
                            },
                          ],
                          onClick: ({ key }) => {
                            if (key === "add-note") {
                              setNoteTarget(r.payload.messageId!);
                              setNoteText("");
                              setRespuestaA(null);
                              return;
                            }
                            if (key.startsWith("react:")) {
                              void doReaccionar(r.payload.messageId!, key.slice("react:".length));
                            }
                          },
                        }}
                      >
                        <Button
                          size="small"
                          type="text"
                          icon={<MoreOutlined style={{ fontSize: 12, color: "#8C8C8C" }} />}
                          style={{ height: 18, width: 18 }}
                        />
                      </Dropdown>
                    )}
                  </Flex>
                </div>
              </div>
            );
          })}

        {/* D56: en automático, el asistente respondiéndole al cliente, al pie del hilo. */}
        {actividadDelPie && (
          <div style={{ margin: "8px 0", textAlign: "right" }}>
            <ActividadDelAsistenteRow actividad={actividadDelPie} />
          </div>
        )}
        {/* Programados pendientes / fallidos (OpenSpec 003) — bloques internos, el contacto no los ve */}
        {scheduledList
          .filter((s) => s.status === "pending" || s.status === "failed")
          .sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt))
          .map((s) => (
            <div
              key={s.scheduledId}
              style={{
                margin: "10px auto",
                maxWidth: "80%",
                padding: "8px 12px",
                borderRadius: 8,
                background: s.status === "failed" ? "#FFF1F0" : "#F0F5FF",
                border: `1px solid ${s.status === "failed" ? "#FFCCC7" : "#D6E4FF"}`,
              }}
            >
              <Flex align="center" justify="space-between" gap={8}>
                <Space size={6}>
                  {s.status === "failed" ? (
                    <WarningOutlined style={{ color: "#FF4D4F" }} />
                  ) : (
                    <ClockCircleOutlined style={{ color: "#2F54EB" }} />
                  )}
                  <Text style={{ fontSize: 13, fontWeight: 600 }}>
                    {s.status === "failed"
                      ? "No se pudo enviar el mensaje programado"
                      : `${s.createdByName} programó un mensaje para el ${dayjs(s.scheduledAt).format("DD/MM HH:mm")}`}
                  </Text>
                </Space>
                <Space size={4}>
                  {s.status === "failed" && canEdit && (
                    <Button size="small" icon={<RedoOutlined />} onClick={() => void doResendScheduled(s)}>
                      Reenviar
                    </Button>
                  )}
                  {s.status === "pending" && canEdit && (
                    <Popconfirm
                      title="¿Cancelar el mensaje programado?"
                      okText="Cancelar envío"
                      cancelText="No"
                      okButtonProps={{ danger: true }}
                      onConfirm={() => void doCancelScheduled(s)}
                    >
                      <Button size="small" danger type="text">
                        Cancelar
                      </Button>
                    </Popconfirm>
                  )}
                </Space>
              </Flex>
              <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 2 }} ellipsis>
                "{s.content}"
              </Text>
            </div>
          ))}
      </div>

      {/* Compose */}
      <div style={{ borderTop: "1px solid #f0f0f0", padding: 12 }}>
        {/* D65: lo que el asistente tiene programado acá; cada una lleva a la nota que la confirma. */}
        <FranjaDeTareas tareas={franja.tareas} onIr={irATarea} />
        {/*
          #177: el cartel va ACÁ y no en un toast porque tiene que estar visible mientras se
          escribe. El bug que arregla es de los peores: el envío se aceptaba, WhatsApp lo rechazaba
          después, y el operador veía un ✓. Dice hasta cuándo, y dice explícitamente que el QR no
          sirve — que es lo que uno intenta cuando ve el canal en rojo.
        */}
        {restricted && cloud && (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 10 }}
            message="Meta restringió este número"
            description={
              <>
                No se pueden enviar mensajes desde el panel{" "}
                <strong>{textoVencimiento(conversation?.channel_restricted_until)}</strong>. Lo que el
                contacto escriba sigue entrando, y las notas internas siguen disponibles.
              </>
            }
          />
        )}
        {!restricted && bloqueo === "pausado" && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 10 }}
            message="El envío por WhatsApp Business está pausado"
            description="Hay un problema con Meta y el envío se pausó. Lo que el contacto escriba sigue entrando, y las notas internas siguen disponibles."
          />
        )}
        {convId && conversation?.channel_id && (
          <TemplatePicker
            open={templatePicker}
            onClose={() => setTemplatePicker(false)}
            conversationId={convId}
            channelId={conversation.channel_id}
            // El de la agenda, como la cabecera: el de WhatsApp suele venir vacío o ser el número.
            contactName={headerContact?.name || conversation.contact_name}
            contactId={headerContact?.contactId ?? undefined}
            userId={headerContact?.userId}
            storeCode={conversation.store_code}
            storeName={
              conversation.participants?.find((p) => p.storeCode === conversation.store_code)?.storeName ??
              headerParticipants.find((p) => p.type === "store")?.identifier
            }
            sellers={headerParticipants.filter((p) => p.type === "seller").map((p) => p.identifier).filter(Boolean)}
            onSent={(sent) => {
              appendLocal(sent);
              if (closed) {
                void conversationsApi.patchStatus(convId, "open").then(() => onStatusChange());
              }
            }}
            onError={(e) => avisarErrorDeEnvio(e, "No se pudo enviar la plantilla")}
          />
        )}
        {/*
          Sin banner (#519): la ventana cerrada es una línea, como la de la ventana abierta, y la
          plantilla sale desde la caja (el botón al lado del clip, o el menú de la flechita).
        */}
        {bloqueo === "ventana" && (
          <Text type="secondary" style={{ display: "block", marginBottom: 8, fontSize: 12 }}>
            <ClockCircleOutlined style={{ color: META_AZUL }} />{" "}
            {plantillaEnviada
              ? "Se mandó una plantilla: la ventana se abre cuando el contacto responda."
              : windowExpiresAt
                ? "Pasaron más de 24 h desde el último mensaje del contacto: sólo sale una plantilla."
                : "El contacto no escribió desde que se conectó el número: sólo sale una plantilla."}{" "}
            <Tooltip title="Por WhatsApp Business sólo se le puede escribir libre dentro de las 24 h de su último mensaje. Lo que se manda desde el celular no la vuelve a abrir: se abre cuando el contacto escribe. Fuera de la ventana sale una plantilla aprobada por Meta; las notas internas siguen disponibles.">
              <InfoCircleOutlined aria-label="Más información" style={{ cursor: "help" }} />
            </Tooltip>
          </Text>
        )}
        {paymentIssue && (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 10 }}
            message="Meta no está entregando los mensajes de este número"
            description="La cuenta de WhatsApp del negocio no tiene un método de pago válido. Lo tiene que cargar un admin del Store en WhatsApp Manager; el aviso también está en la ficha del canal."
          />
        )}
        {cloud && bloqueo === null && windowExpiresAt && (
          <Text type="secondary" style={{ display: "block", marginBottom: 8, fontSize: 12 }}>
            <ClockCircleOutlined /> Se le puede responder por {tiempoRestante(windowExpiresAt, now)} más.
          </Text>
        )}
        {restricted && !cloud && (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 10 }}
            message="WhatsApp restringió este número"
            description={
              <>
                No se pueden enviar mensajes{" "}
                <strong>{textoVencimiento(conversation?.channel_restricted_until)}</strong>. Es una
                restricción de la <strong>cuenta</strong>, no de la sesión:{" "}
                <strong>re-vincular con QR no la levanta</strong>. Mientras dure,{" "}
                <strong>se puede responder desde el teléfono y desde WhatsApp Web</strong> —esos
                mensajes igual entran al panel— y las notas internas siguen disponibles.
              </>
            }
          />
        )}
        {bloqueo !== null && !noteOnly ? (
          <Text type="secondary">
            <WarningOutlined style={{ color: "#FF4D4F" }} /> {RESUMEN_DEL_BLOQUEO[bloqueo]} — no se pueden
            enviar mensajes.
          </Text>
        ) : recording ? (
          <Flex align="center" gap={10} style={{ width: "100%" }}>
            <Button danger type="text" icon={<DeleteOutlined />} onClick={() => stopRecording(false)} />
            <span style={{ width: 10, height: 10, borderRadius: 10, background: "#FF4D4F", display: "inline-block" }} />
            <Text>Grabando… {fmtSecs(recSecs)}</Text>
            <div style={{ flex: 1 }} />
            <Button
              icon={<SendOutlined />}
              loading={sending}
              onClick={() => stopRecording(true)}
            />
          </Flex>
        ) : (
          <>
            {/* La de la ventana ya la explica la línea de arriba. */}
            {noteOnly && bloqueo && bloqueo !== "ventana" && (
              <Text type="secondary" style={{ display: "block", marginBottom: 8, fontSize: 12 }}>
                <WarningOutlined style={{ color: "#FF4D4F" }} /> {RESUMEN_DEL_BLOQUEO[bloqueo]} — no se
                pueden enviar mensajes, pero sí dejar una nota interna.
              </Text>
            )}
            {composingNote && replyTo && (
              <div style={{ marginBottom: 8 }}>
                <CitaDeNota replyTo={replyTo} onClick={irACita} onCancel={() => setReplyTo(null)} />
              </div>
            )}
            {attachment && (
              <Flex
                align="center"
                gap={8}
                style={{ marginBottom: 8, padding: "6px 8px", background: "#f5f5f5", borderRadius: 6 }}
              >
                {attachment.type.startsWith("image/") ? (
                  <img
                    src={URL.createObjectURL(attachment)}
                    alt=""
                    style={{ width: 40, height: 40, objectFit: "cover", borderRadius: 4 }}
                  />
                ) : (
                  <PaperClipOutlined />
                )}
                <Text ellipsis style={{ flex: 1, fontSize: 13 }}>
                  {attachment.name}
                </Text>
                <Button size="small" type="text" icon={<CloseOutlined />} onClick={() => setAttachment(null)} />
              </Flex>
            )}
            <Flex gap={8} align="end">
              <input
                ref={fileInputRef}
                type="file"
                hidden
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  // Se avisa al elegir el archivo, no después de intentar subirlo.
                  if (f && f.size > MEDIA_MAX_BYTES) {
                    antdMessage.error(
                      `"${f.name}" pesa ${(f.size / 1024 / 1024).toFixed(1)} MB y supera el límite de ${MEDIA_MAX_MB} MB`,
                    );
                  } else if (f) {
                    setAttachment(f);
                  }
                  e.target.value = "";
                }}
              />
              <Button
                icon={<PaperClipOutlined />}
                disabled={composingNote}
                onClick={() => fileInputRef.current?.click()}
              />
              {puedeMandarPlantilla && (
                <Tooltip title="Enviar plantilla">
                  <Button
                    aria-label="Enviar plantilla"
                    icon={<FileTextOutlined />}
                    onClick={() => setTemplatePicker(true)}
                    // Con la ventana cerrada es lo único que sale: se destaca en el azul de Meta (#513).
                    style={
                      bloqueo === "ventana"
                        ? { color: META_AZUL, background: META_AZUL_FONDO, borderColor: META_AZUL_BORDE }
                        : undefined
                    }
                  />
                </Tooltip>
              )}
              {composingNote ? (
                <MentionsInput
                  autoFocus
                  autoSize={{ minRows: 1, maxRows: 5 }}
                  placeholder={
                    replyTo
                      ? `Respuesta a ${replyTo.authorName} — el contacto no la ve (Esc cancela)`
                      : "Nota interna — el contacto no la ve (@ para mencionar, Esc cancela)"
                  }
                  value={text}
                  onChange={setText}
                  options={mentionOptions}
                  names={mentionNames}
                  style={{ flex: 1, background: NOTE_BG, borderColor: NOTE_BORDER }}
                  // El foco automático lo dejaba siempre verde, como si se estuviera escribiendo
                  // (#513): vacío lleva el borde normal, el verde aparece con el primer caracter.
                  focusBorderColor={text.trim() ? undefined : "#d9d9d9"}
                  onKeyDown={(e) => {
                    // Con el canal caido el modo nota es forzado: Escape solo limpia, no "vuelve"
                    // a un compose de mensaje que no existe.
                    if (e.key === "Escape") {
                      if (!noteOnly) setNoteMode(false);
                      setText("");
                      setReplyTo(null);
                    }
                  }}
                  onPressEnter={(e) => {
                    if (!e.shiftKey) {
                      e.preventDefault();
                      void doCreateNote(text);
                    }
                  }}
                />
              ) : (
                <Input.TextArea
                  ref={composerRef}
                  autoSize={{ minRows: 1, maxRows: 5 }}
                  placeholder={canEdit ? "Type a message (@ para una nota interna)" : "Type a message"}
                  value={text}
                  onChange={(e) => {
                    const v = e.target.value;
                    // Un `@` como primer caracter es una mención, y las menciones sólo existen en
                    // la nota: pasa al modo nota con el `@` ya escrito. El dropdown lo abre antd al
                    // soltar la tecla, que ya cae en el input de la nota. Sólo con el input vacío
                    // —no al pegar un texto que empieza con `@`— y sin adjunto, que la nota no lleva.
                    if (canEdit && !text && v === "@" && !attachment) {
                      setNoteMode(true);
                    }
                    setText(v);
                  }}
                  onPressEnter={(e) => {
                    if (!e.shiftKey) {
                      e.preventDefault();
                      void doSend(false);
                    }
                  }}
                />
              )}
              {composingNote || text.trim() || attachment ? (
                <Button
                  icon={<SendOutlined />}
                  loading={sending}
                  disabled={composingNote && !text.trim()}
                  title={composingNote ? "Crear nota interna" : "Enviar"}
                  onClick={() => (composingNote ? void doCreateNote(text) : void doSend(false))}
                />
              ) : (
                <Button icon={<AudioOutlined />} title="Grabar audio" onClick={startRecording} />
              )}
              <Popover
                open={programOpen}
                onOpenChange={(v) => !v && setProgramOpen(false)}
                trigger={[]}
                placement="topRight"
                content={
                  <div style={{ width: 250 }}>
                    <Text strong>Programar mensaje</Text>
                    <div style={{ marginTop: 8 }}>
                      {showLaterToday && (
                        <Button type="text" block style={{ textAlign: "left", color: "#595959" }} onClick={() => void doSchedule(laterToday)}>
                          Later today, at 13:00
                        </Button>
                      )}
                      <Button type="text" block style={{ textAlign: "left", color: "#595959" }} onClick={() => void doSchedule(nextMonday)}>
                        {nextMonday.format("dddd")}, at 9:00
                      </Button>
                    </div>
                    <Divider style={{ margin: "8px 0" }} />
                    <Text strong style={{ fontSize: 13 }}>
                      Personalised time
                    </Text>
                    <Flex gap={6} style={{ marginTop: 6 }}>
                      <DatePicker
                        size="small"
                        placeholder="Seleccionar día"
                        value={customDay}
                        onChange={setCustomDay}
                        disabledDate={(d) => d.isBefore(dayjs().startOf("day")) || d.isAfter(dayjs().add(90, "day"))}
                        style={{ flex: 1 }}
                      />
                      <TimePicker
                        size="small"
                        placeholder="Seleccionar hora"
                        format="HH:mm"
                        minuteStep={5}
                        value={customTime}
                        onChange={setCustomTime}
                        style={{ flex: 1 }}
                      />
                    </Flex>
                    <Flex justify="end" gap={8} style={{ marginTop: 12 }}>
                      <Button size="small" onClick={() => setProgramOpen(false)}>
                        Cancel
                      </Button>
                      <Button
                        size="small"
                        type="primary"
                        style={{ background: "#00733B" }}
                        loading={sending}
                        disabled={!customAt || !customAt.isAfter(dayjs().add(1, "minute"))}
                        onClick={() => customAt && void doSchedule(customAt)}
                      >
                        Schedule
                      </Button>
                    </Flex>
                  </div>
                }
              >
                <Dropdown
                  menu={{
                    items: [
                      // Con el canal caido no se ofrece togglear el modo nota: ya es el unico
                      // posible, y "Cancel internal note" dejaria un compose que no puede enviar.
                      ...(canEdit && !noteOnly
                        ? [
                            {
                              key: "internal-note",
                              label: noteMode ? "Cancelar nota interna" : "Nota interna",
                            },
                          ]
                        : []),
                      ...(puedeMandarPlantilla ? [{ key: "template", label: "Enviar plantilla" }] : []),
                      ...(canEdit
                        ? [
                            {
                              key: "program",
                              label: "Programar mensaje",
                              disabled: composingNote || !text.trim(),
                            },
                          ]
                        : []),
                      {
                        key: "send-close",
                        label: "Enviar y cerrar",
                        disabled: composingNote || (!text.trim() && !attachment),
                      },
                      { key: "close", label: closed ? "Reabrir" : "Cerrar" },
                    ],
                    onClick: ({ key }) => {
                      if (key === "internal-note") {
                        setNoteMode((v) => !v);
                        setAttachment(null);
                        setReplyTo(null);
                      }
                      if (key === "template") setTemplatePicker(true);
                      if (key === "program") setProgramOpen(true);
                      if (key === "send-close") void doSend(true);
                      if (key === "close") void toggleClose();
                    },
                  }}
                >
                  <Button icon={noteMode ? <UpOutlined /> : <DownOutlined />} />
                </Dropdown>
              </Popover>
            </Flex>
          </>
        )}
      </div>

      <ContextPanel
        type={contextPanel}
        onClose={() => setContextPanel(null)}
        contactId={headerContact?.contactId ?? undefined}
        userId={headerContact?.userId}
        storeCode={conversation.store_code}
        contactName={name}
        recargar={presupuestosNombrados}
        onShareQuote={(qt) => {
          // Mismo mensaje que comparte la ficha del presupuesto (N6/P8) — de ahí que el texto salga
          // de `quoteShareMessage` y no esté escrito acá. Pero el canal y el contacto ya son los del
          // chat abierto: no hay nada que elegir.
          const draft = quoteShareMessage({
            quoteId: qt.id,
            storeName: qt.storeName,
            storeLink: qt.storeLink,
            shareHash: qt.shareHash,
          });
          if (!draft) {
            antdMessage.error("Falta el link del Store o el hash del presupuesto");
            return;
          }
          // Si venía escribiendo algo, el mensaje va en una línea nueva y no le pisa el texto.
          setText((prev) => (prev.trim() ? `${prev.trim()}\n${draft}` : draft));
        }}
      />
    </div>
  );
};
